import { useEffect, useState } from 'react';
import { api, darf, datum, heuteIso, liste, type Anmelder } from '../api.ts';
import { Karte, Leer } from '../teile/Geruest.tsx';
import {
  Auswahl, Dialog, Fehlerkasten, Formular, Haken, Text, ausFehler,
  useEntwurf, type Fehlerlage,
} from '../teile/Formular.tsx';
import { PollerKarte } from '../teile/Betrieb.tsx';
import { Ortszuordnung, Regelplaene } from '../teile/Stammpflege.tsx';
import { Rahmenvertraege } from '../teile/Rahmenvertraege.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

// Stammdaten: Gemeinden, Firmen, Leistungskatalog.
//
// Marvin, 23.08.2026: „ich als admin muss es anpassen können."
//
// Drei Dinge, die hier anders sind als in einer gewoehnlichen Pflegemaske:
//
//   1. Der Haken „gepflegt" laesst sich nicht setzen. Er wird gerechnet. Im
//      Bestand trugen ihn 23 Gemeinden ohne hinterlegte Mailadresse, und der
//      Versand lief ins Leere, ohne das zu melden.
//   2. Eine Firma mit offenen Terminen laesst sich nicht stilllegen.
//   3. Eine Katalogposition wird nicht geloescht, sondern ausser Kraft
//      gesetzt. Was einmal abgerechnet wurde, muss erklaerbar bleiben.

const GEWERKE = ['TB', 'EB', 'SP']; // .586: DK entfernt
const PORTALWEGE = ['AAZ', 'VAO', 'FAZ', 'POLLER'];

export default function Stammdaten({ ich }: { ich: Anmelder }) {
  const [wo, setWo] = useState('gemeinden');
  const darfPflegen = darf(ich, 'stammdaten.pflegen');

  return (
    <>
      <div className="kopf">
        <h1>Stammdaten</h1>
        <div className="wachsen" />
        {!darfPflegen && (
          <span className="klein leise">
            Nur lesend. Zum Ändern fehlt das Recht "Stammdaten pflegen".
          </span>
        )}
      </div>
      <div className="reiter">
        <button aria-current={wo === 'gemeinden'} onClick={() => setWo('gemeinden')}>
          Gemeinden
        </button>
        {/* .577: DIE FIRMEN STEHEN JETZT UNTER „BENUTZER UND RECHTE", REITER
            „FIRMEN UND TEAMS". Marvin, 02.11.2026: „Ich gehe das alles an
            mehreren Stellen suchen". Ein Ort für Firmen, Teams und Zugänge. */}
        <button aria-current={wo === 'katalog'} onClick={() => setWo('katalog')}>
          Leistungskatalog
        </button>
        {/* „WO IST DER BEREICH?" - HIER.
            ------------------------------------------------------------------
            ORIGINALTEXT MARVIN (18.09.2026)
              „mir fehlen immernoch bei der montage die eigenen positionen zur
               auswahl. diese posionen trage ich selbst ein mit eigenen preisen
               zu jedem sub. wo ist der bereich?"
            und danach, auf den Bildschirm des Leistungskatalogs:
              „du hast deine arbeit nicht gemacht! es ist überhaupt nicht da
               was du gebaut hast."

            ER HAT RECHT GEHABT, UND ZWEIMAL WAR DIE ANTWORT ZU KLEIN. Beim
            ersten Mal wurde der HAKEN umbenannt - der sass im Fenster
            „ändern" einer bestehenden Position, hinter einem Klick, zwischen
            31 Zeilen der Telekom. Beim zweiten Nachsehen kam der schwerere
            Befund dazu: das Fenster „Position anlegen" kannte den Haken
            ÜBERHAUPT NICHT. Eine eigene Position ließ sich gar nicht
            anlegen - nur anlegen, speichern, wieder aufmachen und dann
            ankreuzen.

            **EIN BEREICH IST KEIN HAKEN.** Er hat 3-mal nach einem ORT
            gefragt, nicht nach einer Eigenschaft. Der Ort ist dieser Reiter:
            er steht in der Leiste, er trägt den Namen, unter dem Marvin
            sucht, und er zeigt genau die Positionen, die uns gehören. Der
            Leistungskatalog daneben bleibt, was er ist: die Liste der
            Telekom. */}
        <button aria-current={wo === 'eigene'} onClick={() => setWo('eigene')}>
          Eigene Positionen
        </button>
        {/* DER RAHMENVERTRAG STEHT NEBEN DEM KATALOG, und zwar aus demselben
            Grund, aus dem der Katalog hier steht: es ist die Liste, gegen die
            abgerechnet wird. Marvin, 03.09.2026: „damit wir eine Trennung
            haben, welcher Auftrag dann zukünftig über welchen Rahmenvertrag
            reinkommt und dann entsprechend auch die richtige Bepreisung
            haben." */}
        {darf(ich, 'rahmenvertrag.lesen') && (
          <button aria-current={wo === 'rahmenvertraege'}
                  onClick={() => setWo('rahmenvertraege')}>
            Rahmenverträge
          </button>
        )}
        {/* Die Pollercodes sind Stammdaten und keine Auftragsdaten: sie
            gelten für eine Zone, nicht für ein Haus. Ein eigener Menüpunkt
            für drei Zeilen wäre einer zu viel. */}
        {/* PREISE JE PARTNER STEHEN NEBEN DEN RAHMENVERTRAGSPREISEN.
            Es sind 2 Seiten derselben Position: was WIR bekommen, und was ein
            PARTNER bekommt. Wer den einen pflegt, sieht den anderen. */}
        {darf(ich, 'subpreis.lesen') && (
          <button aria-current={wo === 'subpreise'}
                  onClick={() => setWo('subpreise')}>
            Preise je Partner
          </button>
        )}
        {darf(ich, 'poller.sehen') && (
          <button aria-current={wo === 'poller'} onClick={() => setWo('poller')}>
            Pollercodes
          </button>
        )}
        {/* Ort → Gemeinde entscheidet, WELCHE Behörde die Post bekommt. Das
            ist eine Stammdatenfrage und keine Auftragsfrage. */}
        <button aria-current={wo === 'orte'} onClick={() => setWo('orte')}>
          Ort und Gemeinde
        </button>
        <button aria-current={wo === 'regelplaene'}
                onClick={() => setWo('regelplaene')}>
          Regelpläne
        </button>
        {/* Die Wege nach draussen. Sie stehen im MENÜ, gepflegt werden sie
            hier: eine Adresse ist ein Stammdatum wie eine Behördenmail. */}
        {darf(ich, 'link.pflegen') && (
          <button aria-current={wo === 'links'} onClick={() => setWo('links')}>
            Externe Links
          </button>
        )}
      </div>
      <div className="inhalt">
        {wo === 'gemeinden' && <Gemeinden pflegen={darfPflegen} ich={ich} />}
        {wo === 'katalog'   && <Katalog pflegen={darfPflegen} />}
        {wo === 'eigene'    && <Katalog pflegen={darfPflegen} eigene
                                        zuDenPreisen={() => setWo('subpreise')} />}
        {wo === 'rahmenvertraege' && <Rahmenvertraege />}
        {wo === 'subpreise' && <Partnerpreise />}
        {wo === 'poller'    && <PollerKarte ich={ich} />}
        {wo === 'orte'      && <Ortszuordnung ich={ich} />}
        {wo === 'regelplaene' && <Regelplaene ich={ich} />}
        {wo === 'links'     && <Links ich={ich} />}
      </div>
    </>
  );
}


// ============================================================== Gemeinden

function Gemeinden({ pflegen, ich }: { pflegen: boolean; ich: Anmelder }) {
  const [zeilen, setZeilen] = useState<any[]>([]);
  const [offen, setOffen] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  /* WELCHE GEMEINDEN SOLLEN WEG.
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (03.09.2026)
       „hier bei den gemeinden muss das auch raus. entweder ausblenden, oder
        ich brauche eine löschfunktion generell, denn ich kann garkeine
        gemeinde löschen."

     Es ist beides geworden, und der Filter ist die HÄLFTE davon: er blendet
     nichts aus, er sucht heraus. Eine Gemeinde, an der kein einziger Auftrag
     hängt, ist der Kandidat - was sie WIRKLICH hält, rechnet der Server, und
     zwar mit derselben Anfrage, die danach löscht. */
  const [nurOhne, setNurOhne] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [loeschen, setLoeschen] = useState<string[] | null>(null);

  const laden = () => api.gemeinden().then((r) => {
    setZeilen(r);
    /* WAS ES NICHT MEHR GIBT, BLEIBT NICHT ANGEHAKT. Sonst zeigte die Zahl
       am Knopf nach dem Löschen weiter auf Zeilen, die keine mehr sind. */
    setGewaehlt((a) => a.filter((id) => r.some((g: any) => g.id === id)));
  }).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  const ungepflegt = zeilen.filter((g) => !g.gepflegt).length;
  const ohneAuftrag = zeilen.filter((g) => !Number(g.auftraege ?? 0));
  const sichtbar = nurOhne ? ohneAuftrag : zeilen;

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <b>{zeilen.length} Gemeinden</b>
          {ungepflegt > 0 && (
            <span className="merker warnung">
              {ungepflegt} ohne vollständige Angaben
            </span>
          )}
        </div>
        {pflegen && (
          <button className="knopf haupt" onClick={() => setNeu(true)}>
            Gemeinde anlegen
          </button>
        )}
      </div>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise">
        „Gepflegt" wird gerechnet, nicht gesetzt: es braucht eine Mailadresse
        oder einen Portalweg und den Vordruck der Behörde als Datei. Das
        Bundesland rechnet sich aus der Postleitzahl und fehlt nur dort, wo
        sich 2 Länder einen Postleitzahlbereich teilen.
      </p>

      {/* EINE LEISTE, DIE HERAUSSUCHT, UND KEINE, DIE VERSTECKT. Der zweite
          Chip zeigt genau die Gemeinden, an denen kein Auftrag hängt - die
          Kandidaten zum Aufräumen. Er steht nur da, wenn es welche gibt:
          ein Filter, der immer null trifft, ist kein Filter, sondern ein
          Knopf, der nichts tut. */}
      {pflegen && ohneAuftrag.length > 0 && (
        <div className="reihe" style={{ margin: '10px 0' }}>
          <button className={'knopf filter' + (!nurOhne ? ' gewaehlt' : '')}
                  aria-pressed={!nurOhne}
                  onClick={() => { setNurOhne(false); setGewaehlt([]); }}>
            Alle ({zeilen.length})
          </button>
          <button className={'knopf filter' + (nurOhne ? ' gewaehlt' : '')}
                  aria-pressed={nurOhne}
                  onClick={() => { setNurOhne(true); setGewaehlt([]); }}>
            ohne Aufträge ({ohneAuftrag.length})
          </button>
          <div className="wachsen" />
          {gewaehlt.length > 0 && (
            <button className="knopf gefahr"
                    onClick={() => setLoeschen(gewaehlt)}>
              {gewaehlt.length === 1 ? 'Eine Gemeinde löschen'
                : `${gewaehlt.length} Gemeinden löschen`}
            </button>
          )}
        </div>
      )}

      {/* DIE SPALTE „LAND" IST WEG.
          Marvin, 25.08.2026: „hier kann das land raus. das kannst du dir
          anhand der plz selbst ziehen."
          Er hat recht, und sie stand ausserdem bei allen 26 Gemeinden auf
          „-". An ihre Stelle tritt der Vordruck: das ist die Spalte, an der
          man sieht, warum eine Gemeinde unvollständig ist. */}
      <table className="tabelle">
        <thead>
          <tr>
            {pflegen && (
              <th style={{ width: 34 }}>
                {/* ALLE ANHAKEN, DIE GERADE DASTEHEN - und nicht alle, die es
                    gibt: der Haken gilt für die Liste, die man sieht. */}
                <input type="checkbox" aria-label="Alle anhaken"
                       checked={sichtbar.length > 0
                                && gewaehlt.length === sichtbar.length}
                       onChange={(e) => setGewaehlt(
                         e.target.checked ? sichtbar.map((g: any) => g.id) : [])} />
              </th>
            )}
            <th>Gemeinde</th>
            <th style={{ width: 96 }}>Vorlauf</th>
            <th>Behördenpost</th>
            <th style={{ width: 200 }}>Vordruck VAO</th>
            <th style={{ width: 80 }}>Adressen</th>
            <th style={{ width: 124 }}>Stand</th>
            <th style={{ width: 170 }} />
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((g) => (
            <tr key={g.id}>
              {pflegen && (
                <td>
                  <input type="checkbox"
                         aria-label={`${g.name} anhaken`}
                         checked={gewaehlt.includes(g.id)}
                         onChange={(e) => setGewaehlt((a) => (e.target.checked
                           ? [...a, g.id] : a.filter((x) => x !== g.id)))} />
                </td>
              )}
              <td>
                <b>{g.name}</b>
                {g.ort && g.ort !== g.name && (
                  <div className="klein leise">{g.plz} {g.ort}</div>
                )}
              </td>
              {/* DIE ZAHL GEHÖRT HIN, NICHT IN EINEN TOOLTIP. Hier stand
                  „global" und die Zahl nur beim Darüberfahren - und einen
                  Tooltip gibt es auf dem Telefon nicht. Wer wissen will, wie
                  viel Vorlauf für Koblenz gilt, hat es damit nicht erfahren. */}
              <td className="nichtumbrechen">
                {g.vorlauf_tage === null || g.vorlauf_tage === undefined
                  ? <span className="leise">
                      {g.vorlauf_global ?? '?'} Werktage, global
                    </span>
                  : <>{g.vorlauf_tage} Werktage</>}
              </td>
              <td className="klein">
                {liste(g.behoerde_mail_an).join(', ')
                  || (liste(g.portal_wege).length
                        ? 'über Portal: ' + liste(g.portal_wege).join(', ')
                        : <span className="leise">nichts hinterlegt</span>)}
              </td>
              {/* ZWEI VERSCHIEDENE LÜCKEN, ZWEI VERSCHIEDENE SÄTZE. „Da steht
                  ein Name, aber keine Datei" war bis zum 31.08.2026 eine
                  eigene Auskunft: der Name kam von früher, die Datei lag
                  in der alten Ablage. Beides ist mit Migration 069 gefallen, und damit
                  bleibt eine Frage statt zweier - liegt ein Vordruck da? */}
              <td className="klein">
                {g.vao_vorlage_name
                  ? g.vao_vorlage_name
                  : <span className="leise">nichts hinterlegt</span>}
              </td>
              <td>{g.adressen}</td>
              <td>
                {g.gepflegt
                  ? <span className="merker gruen">gepflegt</span>
                  : <span className="merker warnung">unvollständig</span>}
              </td>
              <td className="knopfleiste">
                <button className="knopf klein" onClick={() => setOffen(g.id)}>
                  {pflegen ? 'ändern' : 'ansehen'}
                </button>
                {pflegen && (
                  <button className="knopf klein"
                          onClick={() => setLoeschen([g.id])}>
                    löschen
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {offen && (
        <GemeindeDialog id={offen} pflegen={pflegen} ich={ich}
                        zu={() => setOffen(null)}
                        fertig={() => { laden(); setOffen(null); }} />
      )}
      {neu && (
        <GemeindeAnlegen zu={() => setNeu(false)}
                         fertig={(id) => { laden(); setNeu(false); setOffen(id); }} />
      )}
      {loeschen && (
        <GemeindenLoeschen ids={loeschen} zu={() => setLoeschen(null)}
                           fertig={() => { setLoeschen(null); setGewaehlt([]);
                                           laden(); }} />
      )}
    </div>
  );
}


function GemeindeDialog({ id, pflegen, ich, zu, fertig }: any) {
  const [g, setG] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.eineGemeinde(id).then((d) => setG({
      ...d,
      behoerde_mail_an: liste(d.behoerde_mail_an).join(', '),
      behoerde_mail_bcc: liste(d.behoerde_mail_bcc).join(', '),
      poller_mail_an: liste(d.poller_mail_an).join(', '),
      portal_wege: liste(d.portal_wege),
    })).catch((f) => setFehler(ausFehler(f)));
  }, [id]);

  const e = useEntwurf<any>(g);
  if (!e.entwurf) {
    return <Dialog titel="Gemeinde" zu={zu}><Fehlerkasten lage={fehler} /></Dialog>;
  }
  const w = e.entwurf;
  const perPortal = (w.portal_wege ?? []).length > 0;

  return (
    <Dialog titel={`Gemeinde ${w.name}`} laeuft={e.laeuft} zu={zu} breite={780}>
      {!w.gepflegt && (w.fehlt ?? []).length > 0 && (
        <div className="hinweis warnung">
          Zum Pflegen fehlt noch: {(w.fehlt ?? []).join('; ')}.
        </div>
      )}
      <Formular geaendert={e.geaendert && pflegen} laeuft={e.laeuft}
                fehler={e.fehler ?? fehler} verwerfen={zu}
                speichern={async () => {
                  e.setLaeuft(true); e.setFehler(null);
                  try { await api.gemeindeAendern(id, e.aenderungen()); fertig(); }
                  catch (f) { e.setFehler(ausFehler(f)); }
                  finally { e.setLaeuft(false); }
                }}>
        <div className="feldreihe">
          <Text name="name" titel="Name" pflicht breite={260} gesperrt={!pflegen}
                wert={w.name} setze={(v) => e.setze('name', v)} fehler={e.fehler} />
          {/* LEER HEISST GLOBAL. Hier stand „Standard sind 7." - ein Satz,
              der stimmte und den Weg verschwieg: die Zahl steht in der
              Konfiguration und lässt sich dort für alle Gemeinden auf einmal
              ändern. Wer das nicht weiss, trägt sie sechsundzwanzigmal von
              Hand ein. */}
          <Text name="vorlauf_tage" titel="Vorlauf in Werktagen" breite={170}
                gesperrt={!pflegen} wert={String(w.vorlauf_tage ?? '')}
                setze={(v) => e.setze('vorlauf_tage', v)} fehler={e.fehler}
                hinweis={'Leer = der globale Wert aus der Konfiguration'
                  + (w.vorlauf_global === null || w.vorlauf_global === undefined
                     ? '' : ` (zurzeit ${w.vorlauf_global} Werktage)`)
                  + '. Eine 0 heißt ausdrücklich: kein Vorlauf nötig.'} />
        </div>

        <h4 style={{ margin: '14px 0 4px' }}>Anschrift der Verwaltung</h4>
        <div className="feldreihe">
          <Text name="verwaltung" titel="Behörde" breite={300} gesperrt={!pflegen}
                wert={w.verwaltung} setze={(v) => e.setze('verwaltung', v)}
                fehler={e.fehler}
                hinweis="steht im Kopf jedes Behördenschreibens" />
          {/* STRASSE UND HAUSNUMMER GETRENNT.
              Marvin, 25.08.2026: „wenn straße im feld steht, warum ist die
              hausnummer nicht extra oder du musst es in den titel mit
              schreiben."
              Getrennt, nicht umbenannt: aus zwei Feldern lässt sich die
              Briefzeile zusammensetzen, aus einem lässt sich die Hausnummer
              nicht wieder herausholen. */}
          <Text name="strasse" titel="Straße" breite={240} gesperrt={!pflegen}
                wert={w.strasse} setze={(v) => e.setze('strasse', v)} fehler={e.fehler} />
          <Text name="hausnummer" titel="Nr." breite={90} gesperrt={!pflegen}
                wert={w.hausnummer} setze={(v) => e.setze('hausnummer', v)}
                fehler={e.fehler} />
        </div>
        <div className="feldreihe">
          {/* PLZ UND ORT GETRENNT.
              Marvin fragte: „gibt das dann bei den variablen probleme zum
              befüllen der dateien?" Nein - die Zeile im Briefkopf wird beim
              Schreiben ZUSAMMENGESETZT und nicht gespeichert. Getrennt ist
              sogar sicherer: in „53505 Altenahr" fällt eine falsche
              Postleitzahl niemandem auf, in einem PLZ-Feld schon. Und das
              Bundesland lässt sich erst dann daraus rechnen. */}
          <Text name="plz" titel="PLZ" breite={110} gesperrt={!pflegen}
                wert={w.plz} setze={(v) => e.setze('plz', v)} fehler={e.fehler} />
          <Text name="ort" titel="Ort" breite={240} gesperrt={!pflegen}
                wert={w.ort} setze={(v) => e.setze('ort', v)} fehler={e.fehler} />
        </div>
        {/* DAS FELD „BUNDESLAND" IST WEG, und zwar ganz.
            Marvin, 25.08.2026: „hier kann das land raus. das kannst du dir
            anhand der plz selbst ziehen."
            Es ist längst so: `bundeslandFuerPlz()` rechnet es aus der
            Postleitzahl der BAUSTELLE - dort zählen die Feiertage, nicht am
            Sitz der Verwaltung -, und der Vorlauf benutzt das seit dem ersten
            Tag. Diese Spalte hat niemand gelesen. Sie hatte genau eine
            Wirkung: sie hielt alle 26 Gemeinden auf „unvollständig". */}
        <div className="feldreihe">
          <Text name="ansprechpartner" titel="Ansprechpartner" breite={240}
                gesperrt={!pflegen} wert={w.ansprechpartner}
                setze={(v) => e.setze('ansprechpartner', v)} fehler={e.fehler} />
          <Text name="email" titel="E-Mail (allgemein)" art="email" breite={240}
                gesperrt={!pflegen} wert={w.email}
                setze={(v) => e.setze('email', v)} fehler={e.fehler} />
          <Text name="telefon" titel="Telefon" art="tel" breite={180}
                gesperrt={!pflegen} wert={w.telefon}
                setze={(v) => e.setze('telefon', v)} fehler={e.fehler} />
        </div>

        <h4 style={{ margin: '14px 0 4px' }}>Wohin die Behördenpost geht</h4>
        <Text name="behoerde_mail_an" titel="Empfänger" breite="100%"
              gesperrt={!pflegen} wert={w.behoerde_mail_an}
              setze={(v) => e.setze('behoerde_mail_an', v)} fehler={e.fehler}
              hinweis="mehrere durch Komma getrennt" />
        <Text name="behoerde_mail_bcc" titel="Blindkopie" breite="100%"
              gesperrt={!pflegen} wert={w.behoerde_mail_bcc}
              setze={(v) => e.setze('behoerde_mail_bcc', v)} fehler={e.fehler} />
        <Text name="poller_mail_an" titel="Empfänger für Polleranträge" breite="100%"
              gesperrt={!pflegen} wert={w.poller_mail_an}
              setze={(v) => e.setze('poller_mail_an', v)} fehler={e.fehler}
              hinweis="leer = dieselben wie oben" />

        <h4 style={{ margin: '14px 0 4px' }}>Portal statt Mail</h4>
        <div className="feldreihe">
          {PORTALWEGE.map((p) => (
            <Haken key={p} name={'portal_' + p} titel={p} gesperrt={!pflegen}
                   wert={(w.portal_wege ?? []).includes(p)}
                   setze={(an) => e.setze('portal_wege', an
                     ? [...(w.portal_wege ?? []), p]
                     : (w.portal_wege ?? []).filter((x: string) => x !== p))} />
          ))}
        </div>
        {perPortal && (
          <Text name="portal_url" titel="Adresse des Portals" pflicht breite="100%"
                gesperrt={!pflegen} wert={w.portal_url}
                setze={(v) => e.setze('portal_url', v)} fehler={e.fehler}
                hinweis="Was über das Portal läuft, wird nicht per Mail versendet.
                         Der Bildschirm sagt dann, wo es einzustellen ist." />
        )}

        <h4 style={{ margin: '14px 0 4px' }}>Vordrucke der Behörde</h4>
        {/* HIER STANDEN ZWEI TEXTFELDER MIT DATEINAMEN.
            Marvin, 25.08.2026: „es fehlt das dropdown zur vorlage ... wo
            liegen diese ganzen dateien und wie lade ich sie hoch?"
            Antwort: nirgends. Es waren Namen von früher, die keine
            Zeile dieses Systems je geöffnet hat - und die trotzdem darüber
            entschieden, ob eine Gemeinde als gepflegt galt. */}
        <Vorlagenfeld art="VAO" titel="Verkehrsrechtliche Anordnung"
                      pflegen={pflegen} ich={ich}
                      wert={w.vao_vorlage_id}
                      setze={(v) => e.setze('vao_vorlage_id', v)} />
        <Vorlagenfeld art="POLLER" titel="Pollerantrag"
                      pflegen={pflegen} ich={ich}
                      wert={w.poller_vorlage_id}
                      setze={(v) => e.setze('poller_vorlage_id', v)} />
        <Haken name="aktiv" titel="Aktiv" wert={!!w.aktiv} gesperrt={!pflegen}
               setze={(v) => e.setze('aktiv', v)} />
      </Formular>
    </Dialog>
  );
}


/**
 * Der Vordruck einer Behörde: auswählen oder hochladen, an derselben Stelle.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „es fehlt das dropdown zur vorlage ... im allgemeinen frage ich mich wo
 *    liegen diese ganzen dateien und wie lade ich sie hoch? ich muss ja
 *    irgendwie innovativen upload haben."
 *
 * KEIN ZWEITER BILDSCHIRM FÜR DEN UPLOAD. Wer hier steht, hat den Vordruck
 * gerade vor sich, weil er ihn gerade gebraucht hat. Ein Menüpunkt
 * „Vorlagenverwaltung" hiesse: Dialog schliessen, woanders hingehen, dort
 * hochladen, zurückkommen, wiederfinden. Vier Schritte für eine Datei.
 *
 * Die Datei geht in die gemeinsame Ablage und steht danach JEDER Gemeinde
 * zur Auswahl. Sechs Gemeinden mit demselben Landesvordruck laden ihn einmal
 * hoch, nicht sechsmal - sechs Kopien wären sechs Stände, von denen fünf
 * altern.
 */
function Vorlagenfeld(
  { art, titel, wert, setze, pflegen, ich }:
  {
    art: string; titel: string; wert: string | null;
    setze: (v: string | null) => void; pflegen: boolean; ich: Anmelder;
  }
) {
  const [alle, setAlle] = useState<any[] | null>(null);
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [befund, setBefund] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfLaden = pflegen && darf(ich, 'vorlage.pflegen');

  const laden = () =>
    api.vorlagen(art).then(setAlle).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [art]);
  useFrisch(laden);

  const gewaehlt = (alle ?? []).find((v) => v.id === wert);

  async function hoch(datei: File) {
    setLaeuft(true); setFehler(null); setBefund(null);
    try {
      const b = await api.vorlageHochladen(datei, art, '');
      await laden();
      setze(b.id);
      setBefund(b);
      setOffen(false);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <div className="vorlagenfeld">
      <div className="reihe">
        <label className="feld" style={{ flex: '1 1 320px' }}>
          <span>{titel}</span>
          <select value={wert ?? ''} disabled={!pflegen || laeuft}
                  onChange={(ev) => setze(ev.target.value || null)}>
            <option value="">kein Vordruck hinterlegt</option>
            {(alle ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({v.dateiname})
              </option>
            ))}
          </select>
        </label>
        {gewaehlt && (
          <a className="knopf klein" href={api.vorlageAdresse(gewaehlt.id)} target="_blank" rel="noreferrer">
            Ansehen
          </a>
        )}
        {darfLaden && (
          <button type="button" className="knopf klein"
                  onClick={() => setOffen(!offen)}>
            {offen ? 'Abbrechen' : 'Hochladen'}
          </button>
        )}
      </div>

      {/* HIER STAND DER NAME VON FRÜHER als Hinweis, WELCHE Datei aus dem
          hier noch fehlt. Er ist mit Migration 069 gefallen: es gibt
          nichts mehr, aus dem er käme, und nichts, aus dem sie nachkäme.
          Was bleibt, ist die Frage, die zu beantworten ist. */}
      {!wert && (
        <div className="klein leise" style={{ marginTop: 4 }}>
          Noch kein Vordruck zugeordnet. Hochladen geht hier oder unter
          „Vorlagen".
        </div>
      )}

      {gewaehlt && (gewaehlt.platzhalter ?? []).length > 0 && (
        <details className="klein" style={{ marginTop: 4 }}>
          <summary>
            {gewaehlt.platzhalter.length} Platzhalter in diesem Vordruck
          </summary>
          <div className="leise" style={{ marginTop: 4 }}>
            {gewaehlt.platzhalter.join(', ')}
          </div>
        </details>
      )}
      {gewaehlt && Number(gewaehlt.gemeinden) > 1 && (
        <div className="klein leise" style={{ marginTop: 4 }}>
          Diesen Vordruck benutzen {gewaehlt.gemeinden} Gemeinden. Wer ihn
          austauscht, ändert ihn für alle.
        </div>
      )}

      {/* AUCH HIER EINE FLÄCHE, KEIN DATEIFELD.
          Marvin, 26.08.2026: „wenn ich dateien hochladen will, dann überall
          per drag and drop, nichts anderes." Hier stand ein nacktes
          `<input type=file>` - der letzte Ort im System, an dem der Weg
          herein anders aussah als überall sonst. */}
      {offen && darfLaden && (
        <div className="hochladefeld">
          <Abwurf
            titel="Den Vordruck hierher"
            klein laeuft={laeuft} laufText="Wird gelesen"
            accept=".docx,.pdf"
            hinweis={'Der Vordruck der Behörde, so wie sie ihn haben will. '
                   + 'Beim Hochladen wird hineingesehen: die Platzhalter '
                   + '({{name}}) werden ausgelesen, und es steht danach da, '
                   + 'welche davon dieses System nicht füllen kann.'}
            nimm={(x) => { if (x[0]) hoch(x[0].datei); }} />
        </div>
      )}

      {/* WAS DER VORDRUCK VERLANGT UND WIR NICHT LIEFERN. Das ist der Grund,
          warum überhaupt hineingesehen wird. Früher fehlte ein
          Pflichtfeld in ALLEN sechs Vorlagen, und niemand konnte es sehen:
          ein leerer Platzhalter im fertigen Papier sieht aus wie ein leeres
          Feld im Vordruck. */}
      {befund && (
        <div className={'hinweis klein '
                        + ((befund.fehlende_felder ?? []).length ? 'warnung' : '')}>
          {befund.meldung}
          {(befund.fehlende_felder ?? []).length > 0 && (
            <div style={{ marginTop: 4 }}>
              <b>Nicht gefüllt:</b> {befund.fehlende_felder.join(', ')}.
              Diese Angaben erfasst das System noch nicht; sie blieben im
              Papier leer.
            </div>
          )}
        </div>
      )}
      <Fehlerkasten lage={fehler} />
    </div>
  );
}


/**
 * Gemeinden löschen - erst die Probe, dann der Grund.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „ich brauche eine löschfunktion generell, denn ich kann garkeine gemeinde
 *    löschen."
 *
 * DIE PROBE UND DER LAUF SIND DERSELBE WEG (`POST /gemeinden/loeschen`, mit
 * und ohne `probe`). Eine zweite Rechnung daneben liefe beim ersten Nachtrag
 * auseinander, und dann zeigte das Fenster etwas anderes an, als der Knopf
 * tut - genau dafür gibt es die Probe.
 *
 * WAS NICHT WEGGEHT, WIRD BEIM NAMEN GENANNT und hält den Rest nicht auf.
 * Andersherum bliebe bei der dritten von elf der halbe Bestand stehen, und
 * niemand wüsste, welche Hälfte.
 */
function GemeindenLoeschen({ ids, zu, fertig }: any) {
  const [probe, setProbe] = useState<any>(null);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.gemeindenLoeschprobe(ids).then(setProbe)
      .catch((f) => setFehler(ausFehler(f)));
  }, []);

  const geht = (probe?.zeilen ?? []).filter((z: any) => z.geht);
  const haelt = (probe?.zeilen ?? []).filter((z: any) => !z.geht);

  async function los() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.gemeindenLoeschen(geht.map((z: any) => z.id), grund.trim());
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel={ids.length === 1 ? 'Gemeinde löschen' : 'Gemeinden löschen'}
            laeuft={laeuft} zu={zu} breite={640}>
      <Fehlerkasten lage={fehler} />
      {!probe ? (
        <p className="klein leise">Es wird nachgesehen, was daran hängt.</p>
      ) : (
        <>
          {/* WAS GELÖSCHT IST, LÄSST SICH NICHT MEHR NACHSEHEN. Deshalb steht
              hier, WAS mitgeht, und nicht nur eine Zahl. */}
          {geht.length > 0 ? (
            <div className="hinweis warnung">
              <b>{geht.length === 1 ? 'Diese Gemeinde geht weg'
                : `Diese ${geht.length} Gemeinden gehen weg`}</b>
              {geht.map((z: any) => (
                <div key={z.id}>
                  {z.name}
                  {(z.loest?.orte > 0 || z.loest?.adressen > 0) && (
                    <span className="leise">
                      , dabei wird die Zuordnung gelöst:
                      {z.loest.orte > 0 ? ` ${z.loest.orte} ignorierte Orte` : ''}
                      {z.loest.orte > 0 && z.loest.adressen > 0 ? ',' : ''}
                      {z.loest.adressen > 0
                        ? ` ${z.loest.adressen} Adressen ohne Auftrag` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="hinweis fehler">
              <b>Keine davon lässt sich löschen.</b>
              An jeder hängt noch etwas.
            </div>
          )}

          {haelt.length > 0 && (
            <div className="hinweis">
              <b>{haelt.length === 1 ? 'Eine bleibt stehen'
                : `${haelt.length} bleiben stehen`}</b>
              {haelt.map((z: any) => (
                <div key={z.id}>
                  <b>{z.name ?? 'unbekannt'}</b>: {z.haelt.join(', ')}
                </div>
              ))}
            </div>
          )}

          {geht.length > 0 && (
            <>
              <Text name="grund" titel="Warum weg?" breite="100%" pflicht
                    wert={grund} setze={(w: string) => setGrund(w)}
                    hinweis="Steht danach im Protokoll, mit Anschrift und Mailadressen." />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf gefahr" disabled={!grund.trim() || laeuft}
                        onClick={los}>
                  {geht.length === 1 ? 'Löschen' : `${geht.length} löschen`}
                </button>
                <button className="knopf" onClick={zu}>Abbrechen</button>
              </div>
            </>
          )}
          {geht.length === 0 && (
            <div className="reihe" style={{ marginTop: 12 }}>
              <button className="knopf" onClick={zu}>Schließen</button>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}


function GemeindeAnlegen({ zu, fertig }: any) {
  const [f, setF] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (k: string, v: string) => setF({ ...f, [k]: v });

  return (
    <Dialog titel="Gemeinde anlegen" laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      <div className="feldreihe">
        <Text name="name" titel="Name" pflicht breite={280} wert={f.name}
              setze={(v) => setze('name', v)} fehler={fehler} />
        {/* Kein Bundesland beim Anlegen: es rechnet sich aus der
            Postleitzahl der Verwaltungsanschrift, und die trägt man im
            nächsten Schritt ein. */}
        <Text name="vorlauf_tage" titel="Vorlauf in Werktagen" breite={170}
              wert={f.vorlauf_tage} setze={(v) => setze('vorlauf_tage', v)}
              fehler={fehler} hinweis="leer = 7" />
      </div>
      <p className="klein leise">
        Der Rest lässt sich gleich danach eintragen. Ohne Mailadresse und ohne
        den Vordruck der Behörde als Datei gilt die Gemeinde als unvollständig.
      </p>
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !f.name}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try { const g = await api.gemeindeAnlegen(f); fertig(g.id); }
                  catch (x) { setFehler(ausFehler(x)); }
                  finally { setLaeuft(false); }
                }}>
          Anlegen
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


// ================================================================= Firmen

export function Firmen({ pflegen }: { pflegen: boolean }) {
  const [zeilen, setZeilen] = useState<any[]>([]);
  const [offen, setOffen] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  const laden = () => api.subunternehmer().then(setZeilen)
    .catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <b>{zeilen.filter((s) => s.aktiv).length} aktive Firmen</b>
        {pflegen && (
          <button className="knopf haupt" onClick={() => setNeu(true)}>
            Firma anlegen
          </button>
        )}
      </div>
      <Fehlerkasten lage={fehler} />
      <table className="tabelle">
        <thead>
          <tr>
            <th style={{ width: 90 }}>Kurz</th><th>Firma</th>
            <th style={{ width: 140 }}>Gewerke</th><th>Bauleitung</th>
            <th style={{ width: 90 }}>Offen</th>
            <th style={{ width: 90 }}>Stand</th><th style={{ width: 90 }} />
          </tr>
        </thead>
        <tbody>
          {zeilen.map((s) => (
            <tr key={s.id}>
              <td className="einfarbig">{s.kurzzeichen}</td>
              <td>
                <b>{s.firma}</b>
                {s.eigenpersonal && <span className="merker">eigen</span>}
              </td>
              <td className="klein">{liste(s.gewerke).join(', ')}</td>
              <td className="klein">{s.bauleiter ?? ''}</td>
              <td>{s.offen}</td>
              <td>
                {s.aktiv ? <span className="merker gruen">aktiv</span>
                         : <span className="merker">stillgelegt</span>}
              </td>
              <td>
                <button className="knopf klein" onClick={() => setOffen(s.id)}>
                  {pflegen ? 'ändern' : 'ansehen'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {offen && (
        <FirmaDialog id={offen} pflegen={pflegen} zu={() => setOffen(null)}
                     fertig={() => { laden(); setOffen(null); }} />
      )}
      {neu && (
        <FirmaAnlegen zu={() => setNeu(false)}
                      fertig={(id: string) => { laden(); setNeu(false); setOffen(id); }} />
      )}
    </div>
  );
}


function FirmaDialog({ id, pflegen, zu, fertig }: any) {
  const [s, setS] = useState<any>(null);
  /* DIE MONTEURE AUS DEM GBGS (.347), für das Feld „Monteur im GBGS". */
  const [monteure, setMonteure] = useState<{ bid: number; name: string }[]>([]);
  useEffect(() => { api.gbgsKatalog().then((k) => setMonteure(k?.ressourcen ?? [])).catch(() => setMonteure([])); }, []);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => {
    api.einSub(id).then((d) => setS({ ...d, gewerke: liste(d.gewerke) }))
      .catch((f) => setFehler(ausFehler(f)));
  }, [id]);

  const e = useEntwurf<any>(s);
  if (!e.entwurf) {
    return <Dialog titel="Firma" zu={zu}><Fehlerkasten lage={fehler} /></Dialog>;
  }
  const w = e.entwurf;

  return (
    <Dialog titel={w.firma} laeuft={e.laeuft} zu={zu} breite={760}>
      <Formular geaendert={e.geaendert && pflegen} laeuft={e.laeuft}
                fehler={e.fehler ?? fehler} verwerfen={zu}
                speichern={async () => {
                  e.setLaeuft(true); e.setFehler(null);
                  try { await api.subAendern(id, e.aenderungen()); fertig(); }
                  catch (f) { e.setFehler(ausFehler(f)); }
                  finally { e.setLaeuft(false); }
                }}>
        <div className="feldreihe">
          {/* DER HINWEIS HIER STIMMTE NICHT.
              Marvin, 25.08.2026: „das kurzzeichen muss nicht in jedem termin
              stehen." Es stand dort auch nur im Auswahlfeld, und da ist es
              jetzt weg.
              WOFÜR ES WIRKLICH DA IST: die Verfügbarkeitsmeldungen aus dem
              Bestand sind darüber verknüpft - früher ist das Kurzzeichen
              die SPALTENÜBERSCHRIFT. Wer es ändert, kappt die Zuordnung von
              1040 Meldungen. Das gehört unter das Feld, nicht eine
              Behauptung über Termine. */}
          <Text name="kurzzeichen" titel="Kurzzeichen" pflicht breite={140}
                gesperrt={!pflegen} wert={w.kurzzeichen}
                setze={(v) => e.setze('kurzzeichen', v)} fehler={e.fehler}
                hinweis="Der Schlüssel der Verfügbarkeitsmeldungen aus dem
                         Bestand. Ändern kappt deren Zuordnung." />
          <Text name="firma" titel="Firma" pflicht breite={320} gesperrt={!pflegen}
                wert={w.firma} setze={(v) => e.setze('firma', v)} fehler={e.fehler} />
          <Text name="bauleiter" titel="Bauleitung" breite={220} gesperrt={!pflegen}
                wert={w.bauleiter} setze={(v) => e.setze('bauleiter', v)}
                fehler={e.fehler} />
        </div>

        <h4 style={{ margin: '14px 0 4px' }}>Gewerke</h4>
        <div className="feldreihe">
          {GEWERKE.map((g) => (
            <Haken key={g} name={'gw_' + g} titel={g} gesperrt={!pflegen}
                   wert={(w.gewerke ?? []).includes(g)}
                   setze={(an) => e.setze('gewerke', an
                     ? [...(w.gewerke ?? []), g]
                     : (w.gewerke ?? []).filter((x: string) => x !== g))} />
          ))}
        </div>
        <p className="klein leise">
          Ein Gewerk lässt sich nicht herausnehmen, solange dort noch etwas
          offen ist. Der Termin verschwände sonst aus jeder Liste und fände
          trotzdem statt.
        </p>

        <h4 style={{ margin: '14px 0 4px' }}>Erreichbarkeit</h4>
        <div className="feldreihe">
          <Text name="email" titel="E-Mail" art="email" breite={260}
                gesperrt={!pflegen} wert={w.email}
                setze={(v) => e.setze('email', v)} fehler={e.fehler} />
          <Text name="telefon" titel="Telefon" art="tel" breite={180}
                gesperrt={!pflegen} wert={w.telefon}
                setze={(v) => e.setze('telefon', v)} fehler={e.fehler} />
          <Text name="mobil" titel="Mobil" art="tel" breite={180}
                gesperrt={!pflegen} wert={w.mobil}
                setze={(v) => e.setze('mobil', v)} fehler={e.fehler} />
          <Text name="telegram_chat_id" titel="Telegram-Chat" breite={180}
                gesperrt={!pflegen} wert={w.telegram_chat_id}
                setze={(v) => e.setze('telegram_chat_id', v)} fehler={e.fehler}
                hinweis={`allgemein: Vorabend-Info und Terminmeldungen${themaText(w.telegram_chat_id)}`} />
          {/* DIE GRUPPE FÜR FREIGABEN IST EINE ANDERE (23.09.2026). Marvin:
              „der chat von der vorabendinfo ist allgemein, die freigaben hat
              eine eigene gruppe." Ohne Eintrag geht die Freigabe nur in die
              Glocke und unter „Freigaben", nie in den allgemeinen Chat. */}
          <Text name="telegram_freigabe_chat_id" titel="Telegram-Gruppe Freigaben" breite={200}
                gesperrt={!pflegen} wert={w.telegram_freigabe_chat_id}
                setze={(v) => e.setze('telegram_freigabe_chat_id', v)} fehler={e.fehler}
                hinweis={`Freigaben zur Abrechnung und Korrekturen${themaText(w.telegram_freigabe_chat_id)}`} />
        </div>
        {/* DIE MATERIAL-GRUPPE (.250). Hier buchen die Monteure der Firma
            Material mit einem Foto vom Etikett aus. Chat-ID und Thema zeigt
            der Bot in der Gruppe auf /chatid. */}
        <div className="feldreihe" style={{ alignItems: 'center' }}>
          <Text name="material_chat_id" titel="Telegram-Gruppe Material" breite={200}
                gesperrt={!pflegen} wert={w.material_chat_id}
                setze={(v) => e.setze('material_chat_id', v)} fehler={e.fehler}
                hinweis="Entnahmen per Foto; /chatid in der Gruppe zeigt die Nummer" />
          <Text name="material_thread_id" titel="Thema" breite={100}
                gesperrt={!pflegen} wert={w.material_thread_id}
                setze={(v) => e.setze('material_thread_id', v)} fehler={e.fehler}
                hinweis="leer: ganze Gruppe" />
          <Haken name="material_aktiv" titel="Material-Bot aktiv" wert={!!w.material_aktiv}
                 gesperrt={!pflegen} setze={(v) => e.setze('material_aktiv', v)} />
        </div>
        <div className="feldreihe">
          <Text name="strasse" titel="Straße" breite={260} gesperrt={!pflegen}
                wert={w.strasse} setze={(v) => e.setze('strasse', v)} fehler={e.fehler} />
          <Text name="plz" titel="PLZ" breite={100} gesperrt={!pflegen}
                wert={w.plz} setze={(v) => e.setze('plz', v)} fehler={e.fehler} />
          <Text name="ort" titel="Ort" breite={200} gesperrt={!pflegen}
                wert={w.ort} setze={(v) => e.setze('ort', v)} fehler={e.fehler} />
        </div>

        <Text name="notiz" titel="Notiz" mehrzeilig gesperrt={!pflegen}
              wert={w.notiz} setze={(v) => e.setze('notiz', v)} fehler={e.fehler} />
        <Haken name="eigenpersonal" titel="Eigenes Personal" gesperrt={!pflegen}
               wert={!!w.eigenpersonal} setze={(v) => e.setze('eigenpersonal', v)} />
        {/* DER MONTEUR IM GBGS (.347). Marvin: „der monteur muss beim buchen
            gewählt werden. das machen wir bei der zuordnung der firma in der
            konfiguration, da kommt ein benutzer bei einer firma". */}
        <Auswahl name="gbgs_ressource_bid" titel="Monteur im GBGS" breite={260} gesperrt={!pflegen}
                 wert={w.gbgs_ressource_bid == null ? '' : String(w.gbgs_ressource_bid)}
                 setze={(v) => e.setze('gbgs_ressource_bid', v === '' ? null : v)}
                 optionen={[
                   ...monteure.map((m) => ({ wert: String(m.bid), text: m.name })),
                   ...(w.gbgs_ressource_bid != null && !monteure.some((m) => String(m.bid) === String(w.gbgs_ressource_bid))
                     ? [{ wert: String(w.gbgs_ressource_bid), text: `Monteur ${w.gbgs_ressource_bid} (nicht mehr im Portal)` }] : []),
                 ]}
                 leerText={monteure.length ? 'keiner: Termine stehen auf der Handliste' : 'noch keine Monteure gelesen (Seite GBGS, Jetzt lesen)'}
                 hinweis="Wer bei einem Termin dieser Firma im GBGS als Ressource gebucht wird." fehler={e.fehler} />
        <Haken name="aktiv" titel="Aktiv" gesperrt={!pflegen}
               wert={!!w.aktiv} setze={(v) => e.setze('aktiv', v)}
               hinweis={w.offen > 0
                 ? `Diese Firma hat ${w.offen} offene Termine. Solange die stehen,
                    lässt sie sich nicht stilllegen.`
                 : undefined} />
        {w.zugaenge > 0 && (
          <p className="klein leise">
            {w.zugaenge} Zugänge hängen an dieser Firma. Sie sehen genau die
            Aufträge, in denen sie ein Gewerk haben.
          </p>
        )}
      </Formular>
    </Dialog>
  );
}


function FirmaAnlegen({ zu, fertig }: any) {
  const [f, setF] = useState<any>({ gewerke: [] });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (k: string, v: any) => setF({ ...f, [k]: v });

  return (
    <Dialog titel="Firma anlegen" laeuft={laeuft} zu={zu} breite={640}>
      <Fehlerkasten lage={fehler} />
      <div className="feldreihe">
        <Text name="kurzzeichen" titel="Kurzzeichen" pflicht breite={140}
              wert={f.kurzzeichen} setze={(v) => setze('kurzzeichen', v)}
              fehler={fehler} />
        <Text name="firma" titel="Firma" pflicht breite={320} wert={f.firma}
              setze={(v) => setze('firma', v)} fehler={fehler} />
      </div>
      <div className="feldreihe">
        {GEWERKE.map((g) => (
          <Haken key={g} name={'gw_' + g} titel={g}
                 wert={f.gewerke.includes(g)}
                 setze={(an) => setze('gewerke', an
                   ? [...f.gewerke, g] : f.gewerke.filter((x: string) => x !== g))} />
        ))}
      </div>
      <div className="feldreihe">
        <Text name="bauleiter" titel="Bauleitung" breite={220} wert={f.bauleiter}
              setze={(v) => setze('bauleiter', v)} fehler={fehler} />
        <Text name="email" titel="E-Mail" art="email" breite={240} wert={f.email}
              setze={(v) => setze('email', v)} fehler={fehler} />
      </div>
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt"
                disabled={laeuft || !f.kurzzeichen || !f.firma}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try { const s = await api.subAnlegen(f); fertig(s.id); }
                  catch (x) { setFehler(ausFehler(x)); }
                  finally { setLaeuft(false); }
                }}>
          Anlegen
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


// ================================================================ Katalog

/**
 * DIE POSITIONEN - EINMAL ALS LISTE DER TELEKOM, EINMAL ALS UNSERE EIGENEN.
 *
 * `eigene` entscheidet, WELCHE Hälfte dieser Bildschirm zeigt. Es ist
 * absichtlich EIN Bauteil und keine zweite Abschrift: die Spalten, das
 * Ändern-Fenster, die Regel „nicht löschen, sondern ausser Kraft setzen" und
 * die Gegenproben am Server sind für beide dieselben. **Zwei Abschriften
 * derselben Liste laufen beim ersten Nachtrag auseinander** - dieselbe Lehre
 * wie bei der Maskierung in `routen/auftrag.ts`.
 *
 * Was sich unterscheidet, ist genau das, was sich unterscheiden MUSS: die
 * Überschrift, der Knopf zum Anlegen (er setzt den Haken gleich mit), der
 * Weg zu den Preisen und die Zeile, die dasteht, wenn noch nichts da ist.
 */
function Katalog({ pflegen, eigene, zuDenPreisen }: {
  pflegen: boolean; eigene?: boolean; zuDenPreisen?: () => void;
}) {
  const [zeilen, setZeilen] = useState<any[]>([]);
  const [alle, setAlle] = useState(false);
  const [offen, setOffen] = useState<any>(null);
  const [neu, setNeu] = useState(false);
  const [einlesen, setEinlesen] = useState(false);
  /** Die Position, deren Firmen gerade offen stehen. */
  const [firmen, setFirmen] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  const laden = () => (pflegen ? api.lvAlle() : api.lv())
    .then(setZeilen).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [pflegen]);
  useFrisch(laden);

  /* DER NEUE REITER NIMMT DEM KATALOG NICHTS WEG, ER SUCHT HERAUS.
     ------------------------------------------------------------------------
     GEMESSEN AM BESTAND, BEVOR GETEILT WURDE, und das Ergebnis hat die
     Bauart geändert: 5 Positionen tragen den Haken schon, und es sind
     ORDNUNGSZAHLEN DER TELEKOM - 10030340, 10030360, 10030364, 10030366,
     10030368. Sie sind keine „selbst eingetragenen" Positionen; der Haken
     hiess ursprünglich „feste Montageposition" und entscheidet, in welchen
     Block der Auswertung eine Leistung zählt.

     **EINE ANSICHT, DIE ZEILEN AUS DEM KATALOG VERSCHWINDEN LÄSST, IST KEINE
     ANSICHT, SONDERN EINE UMSORTIERUNG DES BESTANDES.** Wer morgen die 31
     Positionen der Telekom sucht, fände 26. Deshalb zeigt der
     Leistungskatalog weiterhin ALLE, und dieser Reiter sucht die heraus, um
     die es Marvin geht. Nichts geht verloren, und die Frage, ob „feste
     Montageposition" und „eigene Position" dasselbe sein sollen, entscheidet
     Marvin und nicht diese Zeile. */
  const ohneAusgelaufene = alle ? zeilen : zeilen.filter((l) => !l.ausgelaufen);
  /* GEFILTERT WIRD NACH `eigene_position` UND NICHT NACH `montage_fest`.
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (18.09.2026, auf die Rueckfrage aus dem vorigen Lauf)
       „nein, es sind eigene, freie positionen die ich anlegen will, egal wie
        viele. und die kann ich dann zuweisen zu jeder firma einzeln manuell,
        denn es kann sein, dass jede firma für die gleiche position andere
        preise hat."

     ER HAT DIE FRAGE BEANTWORTET, UND DIE ANTWORT WAR NEIN. `montage_fest`
     haengt an 5 ORDNUNGSZAHLEN DER TELEKOM; es heisst „diese Position der
     Telekom zaehlt in der Auswertung zum Block Montage". Was er will, sind
     Positionen, die es im Verzeichnis der Telekom GAR NICHT GIBT. Seit
     Migration 265 ist das ein eigenes Merkmal, und die 5 sind unangetastet
     geblieben - eine Migration, die sie umdeutet, haette ihm die
     Entscheidung abgenommen. */
  const sichtbar = eigene
    ? ohneAusgelaufene.filter((l) => !!l.eigene_position)
    : ohneAusgelaufene;

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <b>
          {sichtbar.length} {eigene ? 'eigene Positionen' : 'Positionen'}
        </b>
        <div className="reihe">
          {pflegen && (
            <button className="knopf" onClick={() => setAlle(!alle)}>
              {alle ? 'nur gültige' : 'auch ausgelaufene'}
            </button>
          )}
          {/* EIN GANZES VERZEICHNIS AUF EINMAL.
            *
            * ORIGINALTEXT MARVIN (31.08.2026)
            *   „für Leistungspositionen gibt es neue Preise. Preise sind aber
            *    nicht drin. Dann fällt das zumindest raus. aber der muss
            *    zumindest mal dann die Datei einlesen können."
            *
            * Einunddreissig Positionen von Hand nachzupflegen ist eine
            * Nachmittagsarbeit, bei der man sich bei der zwölften vertippt. */}
          {/* DAS VERZEICHNIS DER TELEKOM WIRD EINGELESEN, UNSERE EIGENEN
              POSITIONEN NICHT: es gibt keine Datei, aus der sie kämen -
              Marvin trägt sie selbst ein. Ein Knopf, der hier nichts zu tun
              hätte, ist eine Einladung ins Leere. */}
          {pflegen && !eigene && (
            <button className="knopf" onClick={() => setEinlesen(true)}>
              Verzeichnis einlesen
            </button>
          )}
          {pflegen && eigene && zuDenPreisen && (
            <button className="knopf" onClick={zuDenPreisen}>
              Preise je Partner
            </button>
          )}
          {pflegen && (
            <button className="knopf haupt" onClick={() => setNeu(true)}>
              {eigene ? 'Eigene Position anlegen' : 'Position anlegen'}
            </button>
          )}
        </div>
      </div>
      <Fehlerkasten lage={fehler} />
      {/* DER ERKLÄRTEXT ÜBER DER TABELLE IST WEG.
          Marvin, 18.09.2026: „der hinweistext oben kann weg."
          Er stand auf JEDEM Aufruf dieses Bildschirms und erklärte eine
          Regel, die genau dann greift, wenn man sie anfasst - und DORT steht
          sie weiterhin: das Fenster „ändern" sagt „An dieser Position hängen
          N Aufmaßzeilen. Ordnungszahl und Einheit sind deshalb
          festgestellt", und der Server hält es ohnehin. **Eine Erklärung, die
          immer dasteht, liest nach dem dritten Mal niemand mehr; eine, die
          im Weg steht, wenn sie gilt, ist nicht zu übersehen.** */}
      {eigene && (
        <p className="klein leise" style={{ marginTop: 0, marginBottom: 10 }}>
          Diese Positionen gibt es im Verzeichnis der Telekom nicht - wir
          legen sie selbst an, beliebig viele, mit frei bestimmter
          Ordnungszahl, Text und Einheit. Sie stehen im Aufmaß in der Gruppe
          „Eigene Positionen" ganz oben und gehen in <b>keine</b> LEB-Datei.
          <b> Zugewiesen wird über den Preis:</b> sobald unter „Preise je
          Partner" für eine Firma ein Preis hinterlegt ist, steht die Position
          dieser Firma zur Wahl - dieselbe Position kann bei jeder Firma einen
          anderen Preis haben. Ohne Preis erfasst die Firma sie nicht; der
          Innendienst sieht sie immer.
        </p>
      )}

      {/* EINE LEERE TABELLE IST KEINE AUSKUNFT.
          Genau das war Marvins Erlebnis: er hat gesucht, wo seine eigenen
          Positionen stehen, und eine Liste der Telekom gefunden. Ein Reiter,
          der ohne ein Wort nichts zeigt, ist dasselbe noch einmal - deshalb
          steht hier, was zu tun ist, und der Knopf dazu steht oben. */}
      {sichtbar.length === 0 && (
        <Leer text={eigene
          ? 'Hier steht noch keine eigene Position. Über „Eigene Position '
            + 'anlegen" oben rechts kommt die erste herein: Ordnungszahl, '
            + 'Text und Einheit bestimmen Sie frei, und es dürfen beliebig '
            + 'viele werden. Danach unter „Preise je Partner" je Firma einen '
            + 'Preis eintragen - erst damit steht die Position dieser Firma '
            + 'zur Wahl, und jede Firma darf einen anderen Preis haben. Eine '
            + 'Position, die es im Leistungskatalog schon gibt, holen Sie '
            + 'hierher, indem Sie sie dort mit „ändern" öffnen und den Haken '
            + '„Eigene Position" setzen.'
          : 'Keine Position in dieser Ansicht.'} />
      )}

      {sichtbar.length > 0 && (
      <table className="tabelle">
        <thead>
          <tr>
            <th style={{ width: 110 }}>OZ</th><th>Text</th>
            <th style={{ width: 60 }}>Einh.</th>
            <th style={{ width: 130 }}>Gewerke</th>
            <th style={{ width: 120 }}>Abschnitt</th>
            <th style={{ width: 80 }}>Zeilen</th>
            <th style={{ width: 150 }}>Gültig</th>
            <th style={{ width: 90 }} />
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((l) => (
            <tr key={l.id ?? l.oz}
                style={l.ausgelaufen ? { opacity: .55 } : undefined}>
              <td className="einfarbig">{l.oz}</td>
              <td>
                {l.text}
                {/* DER RANG WIRD GERECHNET, DER HAKEN IST NUR NOCH DER
                    RÜCKFALL. „In 47 Aufträgen" ist eine Auskunft, „häufig"
                    eine Behauptung von vorgestern. */}
                {l.rang != null && l.rang <= 10 && (
                  <span className="merker" title={`In ${l.benutzt_in_auftraegen} `
                    + 'Aufträgen erfasst - das ist Rang ' + l.rang + '.'}>
                    Nr. {l.rang}
                  </span>
                )}
                {/* DIE MARKE HIESS „MONTAGE" UND HEISST JETZT WIE DER
                    REITER. Auf Marvins Bildschirm vom 18.09.2026 stand sie
                    an keiner der 7 sichtbaren Zeilen - die 5, die sie
                    tragen, stehen weiter unten in einer Liste von 31. Ein
                    Wort, das man erst nach dem Rollen findet, ist kein
                    Bereich; der Reiter daneben ist einer. Im Reiter „Eigene
                    Positionen" selbst steht sie nicht: dort trägt sie jede
                    Zeile, und was überall steht, sagt nichts. */}
                {/* ZWEI MERKMALE, ZWEI MARKEN - und sie heissen verschieden,
                    weil sie Verschiedenes sagen. „Eigene Position": gibt es
                    im Verzeichnis der Telekom nicht. „Montage": eine Position
                    der Telekom, die in der Auswertung zum Block Montage
                    zaehlt. Eine Marke fuer beide war der Fehler, nach dem
                    Marvin am 18.09.2026 zum dritten Mal gefragt hat. */}
                {l.eigene_position && !eigene && (
                  <span className="merker" title="Gibt es im Verzeichnis der
                        Telekom nicht. Steht im Reiter „Eigene Positionen“.">
                    Eigene Position
                  </span>
                )}
                {l.montage_fest && (
                  <span className="merker" title="Position der Telekom, die in
                        der Auswertung zum Block „Montage, feste Positionen“
                        zaehlt.">
                    Montage
                  </span>
                )}
                {l.nur_innendienst && <span className="merker">Innendienst</span>}
              </td>
              <td>{l.einheit}</td>
              {/* LEER HEISST ALLE, und das steht auch da: ein leeres Feld
                  liest sich wie ein Pflegefehler. */}
              <td className="klein">
                {liste(l.gewerke).length
                  ? liste(l.gewerke).join(', ')
                  : <span className="leise">alle</span>}
              </td>
              <td className="klein">{liste(l.erlaubte_abschnitte).join(', ')}</td>
              <td>{l.zeilen ?? ''}</td>
              <td className="klein">
                ab {datum(l.gueltig_ab)}
                {l.gueltig_bis && <div>bis {datum(l.gueltig_bis)}</div>}
              </td>
              <td className="knopfleiste">
                {/* DIE ZUWEISUNG STEHT AN DER ZEILE UND NICHT IN EINEM
                    ZWEITEN REITER.
                    ==========================================================
                    ORIGINALTEXT MARVIN (18.09.2026)
                      „wo weise ich die position denn dem Partner zu? Die
                       funktion fehlt komplezz!"

                    ER HAT RECHT GEHABT, UND ES WAR EINE FRAGE DER RICHTUNG.
                    Die Zuweisung IST der Preis, und den gab es - aber nur von
                    der FIRMA aus gedacht („Preise je Partner": was bekommt
                    diese Firma für alles). Wer eine Position gerade angelegt
                    hat, stellt die umgekehrte Frage: **wer bekommt DIESE
                    Position** - und dafür gab es keinen Weg. Ein Wert, den
                    man nur über einen Umweg erreicht, ist für den, der ihn
                    sucht, nicht vorhanden. */}
                {pflegen && l.id && eigene && (
                  <button className="knopf klein"
                          onClick={() => setFirmen(l)}
                          title="Welche Firmen bekommen diese Position, und
                                 zu welchem Preis">
                    Firmen zuweisen
                  </button>
                )}
                {pflegen && l.id && (
                  <button className="knopf klein" onClick={() => setOffen(l)}>
                    ändern
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {offen && (
        <KatalogDialog l={offen} zu={() => setOffen(null)}
                       fertig={() => { laden(); setOffen(null); }} />
      )}
      {neu && (
        <KatalogAnlegen eigene={!!eigene} zu={() => setNeu(false)}
                        fertig={() => { laden(); setNeu(false); }} />
      )}
      {einlesen && (
        <LvEinlesen zu={() => setEinlesen(false)}
                    fertig={() => { setEinlesen(false); laden(); }} />
      )}
      {firmen && (
        <PositionZuweisen l={firmen} zu={() => setFirmen(null)} />
      )}
    </div>
  );
}


/**
 * WER BEKOMMT DIESE POSITION - UND ZU WELCHEM PREIS.
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (18.09.2026, im Reiter „Eigene Positionen")
 *   „wo weise ich die position denn dem Partner zu? Die funktion fehlt
 *    komplezz!"
 *
 * ER HAT RECHT GEHABT. Die Zuweisung IST der Preis - so gebaut am selben Tag,
 * und richtig gedacht. Nur war sie ausschliesslich von der FIRMA aus
 * erreichbar: „Preise je Partner" beantwortet „was bekommt diese Firma fuer
 * alles". Wer eine Position gerade angelegt hat, stellt die andere Frage.
 * **Ein Wert, den man nur ueber einen Umweg erreicht, ist fuer den, der ihn
 * sucht, nicht vorhanden** - dieselbe Lehre wie beim Haken, der 3-mal nicht
 * gefunden wurde.
 *
 * ALLE FIRMEN STEHEN DA, AUCH DIE OHNE PREIS. Eine Liste, die nur die
 * zugewiesenen nennt, beantwortet „wem fehlt sie noch" nicht - und genau die
 * Frage stellt man hier.
 *
 * EIN LEERES FELD NIMMT DIE ZUWEISUNG WEG, und zwar ab heute und nicht
 * rueckwirkend: die Frage „was galt im Maerz" muss beantwortbar bleiben. Das
 * entscheidet der Server (`PUT /subunternehmer/:id/preise`), hier steht nur
 * der Satz dazu.
 *
 * GESPEICHERT WIRD JE ZEILE UND NICHT IN EINEM ZUG. Wer 12 Firmen pflegt und
 * sich bei der siebten vertippt, verloere sonst die 6 davor.
 */
function PositionZuweisen({ l, zu }: any) {
  const [d, setD] = useState<any>(null);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [ab, setAb] = useState(heuteIso());
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  const laden = () => api.lvFirmen(l.id)
    .then((x: any) => {
      setD(x);
      /* DER ENTWURF STARTET AUF DEM STAND und nicht leer: ein leeres Feld
         heisst hier „Zuweisung weg", und ein Formular, das beim Oeffnen alles
         wegnehmen wuerde, ist eine Falle. */
      const e: Record<string, string> = {};
      for (const f of x.firmen ?? []) {
        e[f.id] = f.einheitspreis === null || f.einheitspreis === undefined
          ? '' : String(f.einheitspreis);
      }
      setEntwurf(e);
    })
    .catch((f: any) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [l.id]);
  /* UND ER ZIEHT NACH, WENN DER BILDSCHIRM WIEDER NACH VORNE KOMMT.
     Die Hausregel (Prüfstand 134): wer laedt, meldet es - sonst steht hier
     der Preis von vorhin, waehrend ihn jemand anders gerade geaendert hat.
     Ein Preis, den 2 Leute gleichzeitig pflegen, ist genau der Fall, fuer
     den es die Regel gibt. */
  useFrisch(() => { laden(); });

  async function sichern(firma: string) {
    setLaeuft(firma); setFehler(null);
    try {
      await api.subPreisSetzen(firma, {
        lv_position_id: l.id,
        einheitspreis: entwurf[firma] ?? '',
        gueltig_ab: ab,
      });
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(null); }
  }

  return (
    <Dialog titel={`Firmen zuweisen: ${l.oz}`} untertitel={l.text}
            breite={760} zu={zu} laeuft={!!laeuft}>
      <Fehlerkasten lage={fehler} />
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <div className="hinweis" style={{ marginTop: 0 }}>
            <b>Zugewiesen wird über den Preis.</b>
            Sobald hier ein Preis steht, bekommt diese Firma die Position im
            Aufmaß zur Wahl - und jede Firma darf einen anderen Preis haben.
            Ein leeres Feld nimmt die Zuweisung ab dem gewählten Tag weg; was
            vorher galt, bleibt stehen. Der Innendienst sieht die Position
            immer.
          </div>

          <div className="feldreihe">
            <Text name="gueltig_ab" titel="Gilt ab" art="date" breite={200}
                  wert={ab} setze={setAb}
                  hinweis="Ein neuer Preis löst den alten ab diesem Tag ab. Er
                           überschreibt ihn nicht - sonst wäre nicht mehr zu
                           beantworten, was im März galt." />
            <div className="feld">
              <label>Zugewiesen</label>
              <div style={{ paddingTop: 6 }}>
                <b>{d.zugewiesen}</b> von {d.firmen.length} Firmen
              </div>
            </div>
          </div>

          <table className="tabelle">
            <thead>
              <tr>
                <th>Firma</th>
                <th style={{ width: 90 }}>Gewerke</th>
                <th style={{ width: 150 }}>Preis je {l.einheit}</th>
                <th style={{ width: 120 }}>gilt seit</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {d.firmen.map((f: any) => (
                <tr key={f.id}>
                  <td>
                    <b>{f.firma}</b>
                    <div className="klein leise">{f.kurzzeichen}</div>
                  </td>
                  <td className="klein">
                    {(f.gewerke ?? []).join(', ') || <span className="leise">keine</span>}
                  </td>
                  <td>
                    <input value={entwurf[f.id] ?? ''} inputMode="decimal"
                           aria-label={`Preis für ${f.firma}`}
                           disabled={!d.darfPflegen || laeuft === f.id}
                           placeholder="nicht zugewiesen"
                           onChange={(e) => setEntwurf(
                             { ...entwurf, [f.id]: e.target.value })} />
                  </td>
                  <td className="klein leise">
                    {f.gueltig_ab ? datum(f.gueltig_ab) : ''}
                  </td>
                  <td className="knopfleiste">
                    {d.darfPflegen && (
                      <button className="knopf klein"
                              disabled={laeuft === f.id
                                || (entwurf[f.id] ?? '')
                                   === (f.einheitspreis === null
                                        || f.einheitspreis === undefined
                                          ? '' : String(f.einheitspreis))}
                              onClick={() => void sichern(f.id)}>
                        {laeuft === f.id ? 'Einen Moment' : 'Sichern'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.darfPflegen && (
            <p className="klein leise">
              Nur lesend. Zum Ändern fehlt das Recht „Preise je Partner
              pflegen".
            </p>
          )}
        </>
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        {/* „Fertig" und nicht „Schließen": gespeichert wird je Zeile, dieses
            Fenster hat nichts mehr zu sichern. */}
        <button className="knopf haupt" onClick={zu}>Fertig</button>
      </div>
    </Dialog>
  );
}


function KatalogDialog({ l, zu, fertig }: any) {
  const e = useEntwurf<any>({
    oz: l.oz, text: l.text, einheit: l.einheit,
    nachweis: l.nachweis ?? '', haeufig: !!l.haeufig,
    nur_innendienst: !!l.nur_innendienst,
    gewerke: (l.gewerke ?? []).join(','),
    montage_fest: !!l.montage_fest,
    eigene_position: !!l.eigene_position,
    reihenfolge: String(l.reihenfolge ?? 0),
    gueltig_bis: l.gueltig_bis ? String(l.gueltig_bis).slice(0, 10) : '',
    updated_at: l.updated_at,
  });
  if (!e.entwurf) return null;
  const w = e.entwurf;
  const haengt = Number(l.zeilen ?? 0) > 0;

  return (
    <Dialog titel={`Position ${l.oz}`} laeuft={e.laeuft} zu={zu} breite={680}>
      {haengt && (
        <div className="hinweis">
          An dieser Position hängen {l.zeilen} Aufmaßzeilen. Ordnungszahl und
          Einheit sind deshalb festgestellt.
        </div>
      )}
      <Formular geaendert={e.geaendert} laeuft={e.laeuft} fehler={e.fehler}
                verwerfen={zu}
                speichern={async () => {
                  e.setLaeuft(true); e.setFehler(null);
                  try { await api.lvAendern(l.id, e.aenderungen()); fertig(); }
                  catch (f) { e.setFehler(ausFehler(f)); }
                  finally { e.setLaeuft(false); }
                }}>
        <div className="feldreihe">
          <Text name="oz" titel="Ordnungszahl" pflicht breite={160} gesperrt={haengt}
                wert={w.oz} setze={(v) => e.setze('oz', v)} fehler={e.fehler} />
          <Auswahl name="einheit" titel="Einheit" pflicht breite={120}
                   gesperrt={haengt} wert={w.einheit} fehler={e.fehler}
                   setze={(v) => e.setze('einheit', v)} leerText=""
                   optionen={['m', 'm2', 'St', 'LE'].map((x) =>
                     ({ wert: x, text: x }))} />
          <Text name="reihenfolge" titel="Reihenfolge" breite={130}
                wert={w.reihenfolge} setze={(v) => e.setze('reihenfolge', v)}
                fehler={e.fehler} />
        </div>
        <Text name="text" titel="Text" pflicht breite="100%" wert={w.text}
              setze={(v) => e.setze('text', v)} fehler={e.fehler} />
        <Text name="nachweis" titel="Verlangter Nachweis" breite="100%"
              wert={w.nachweis} setze={(v) => e.setze('nachweis', v)}
              fehler={e.fehler} />
        {/* WER DIE GEWERKE ENGER ZIEHT, NIMMT DIE POSITION JEMANDEM WEG.
            Deshalb steht das hier und nicht in einer Migration: es ist eine
            Entscheidung, die ein Mensch verantwortet. Migration 080 hat nur
            zugeordnet, was früher BELEGT ist; alles übrige steht bewusst auf „alle". */}
        <GewerkeWahl wert={String(w.gewerke ?? '')}
                     setze={(v) => e.setze('gewerke', v)} />
        <div className="feldreihe">
          {/* DER HAKEN SCHALTET ZWEIERLEI, UND BEIDES GEHÖRT AN IHN
              (08.09.2026). Er stand hier nur mit seiner Wirkung beim Monteur,
              und Marvin hat nach dem anderen gefragt: „Wo trage ich die festen
              Positionen für die Montage ein, die in die Gesamtauswertung
              gehen". Ein Haken, der die halbe Wirkung nennt, wird für den
              anderen Zweck nicht gefunden. */}
          {/* UND SEIT DEM 18.09.2026 SAGT ER AUCH, WIE DIE GRUPPE HEISST.
              ORIGINALTEXT MARVIN (18.09.2026)
                „mir fehlen immernoch bei der montage die eigenen positionen
                 zur auswahl. ... wo ist der bereich?"
              Der Bereich war dieser Haken. Er hiess nur anders als die
              Gruppe, in der die Position danach steht. */}
          {/* ZWEI HAKEN, WEIL ES ZWEI SACHEN SIND.
              ORIGINALTEXT MARVIN (18.09.2026)
                „nein, es sind eigene, freie positionen die ich anlegen will"
              Hier stand EIN Haken mit dem Namen „Eigene Position (feste
              Montageposition)" - eine Klammer, die 2 Bedeutungen in einen
              Namen presst. Seit Migration 265 sind sie getrennt, und zwar
              auch hier: sonst schaltete ein Haken weiter beides. */}
          <Haken name="eigene_position" titel="Eigene Position"
                 wert={!!w.eigene_position}
                 setze={(v) => e.setze('eigene_position', v)}
                 hinweis="Diese Position gibt es im Verzeichnis der Telekom
                          nicht - wir haben sie selbst angelegt. Sie steht im
                          Reiter „Eigene Positionen“, im Aufmaß in der
                          gleichnamigen Gruppe ganz oben und geht in KEINE
                          LEB-Datei. Eine Partnerfirma bekommt sie zur Wahl,
                          sobald für sie unter „Preise je Partner“ ein Preis
                          hinterlegt ist." />
          <Haken name="montage_fest" titel="Feste Montageposition"
                 wert={!!w.montage_fest}
                 setze={(v) => e.setze('montage_fest', v)}
                 hinweis="Eine Position DER TELEKOM, die in der Auswertung zum
                          Block „Montage, feste Positionen“ zählt und im
                          Aufmaß mit oben steht. Nicht dasselbe wie „Eigene
                          Position“: dieser Haken sagt nichts darüber, wer die
                          Position erfunden hat." />
          <Haken name="haeufig" titel="Häufig gebraucht" wert={!!w.haeufig}
                 setze={(v) => e.setze('haeufig', v)}
                 hinweis="Nur noch der Rückfall: solange Aufmaße erfasst sind,
                          zählt das System die 10 häufigsten selbst." />
          <Haken name="nur_innendienst" titel="Nur Innendienst"
                 wert={!!w.nur_innendienst}
                 setze={(v) => e.setze('nur_innendienst', v)} />
        </div>
        {Number(l.benutzt_in_auftraegen ?? 0) > 0 && (
          <p className="klein leise">
            Diese Position ist in {l.benutzt_in_auftraegen} Aufträgen erfasst
            {l.rang != null && ` - das ist Rang ${l.rang}`}.
          </p>
        )}
        <Text name="gueltig_bis" titel="Gültig bis" art="date" breite={200}
              wert={w.gueltig_bis} setze={(v) => e.setze('gueltig_bis', v)}
              fehler={e.fehler}
              hinweis="Ab diesem Tag wird die Position nicht mehr angeboten.
                       Bestehende Aufmaßzeilen bleiben." />
      </Formular>
      <div style={{ marginTop: 16, borderTop: '1px solid var(--rand,#e5e7eb)',
                    paddingTop: 10 }}>
        <button className="knopf" disabled={e.laeuft}
                onClick={async () => {
                  e.setLaeuft(true); e.setFehler(null);
                  try { await api.lvAusserKraft(l.id); fertig(); }
                  catch (f) { e.setFehler(ausFehler(f)); }
                  finally { e.setLaeuft(false); }
                }}>
          Ab heute außer Kraft setzen
        </button>
      </div>
    </Dialog>
  );
}


/**
 * EINE POSITION ANLEGEN - UND SEIT DEM 18.09.2026 AUCH EINE EIGENE.
 *
 * HIER SASS DER FEHLER, NACH DEM MARVIN ZWEIMAL GEFRAGT HAT. Dieses Fenster
 * kannte den Haken `montage_fest` ÜBERHAUPT NICHT. Wer eine eigene Position
 * anlegen wollte, musste sie zuerst als gewöhnliche Katalogposition anlegen,
 * die Liste wieder aufmachen, auf „ändern" klicken und dort ankreuzen - und
 * dazwischen stand sie als Position der Telekom im Katalog.
 *
 * **EIN WEG, DER NUR ÜBER EINEN UMWEG GEHT, IST KEIN WEG.** Der Haken steht
 * jetzt hier, und wer aus dem Reiter „Eigene Positionen" kommt, findet ihn
 * gesetzt vor: von dort aus kann nichts anderes gemeint sein.
 */
function KatalogAnlegen({ zu, fertig, eigene }: any) {
  const [f, setF] = useState<any>({ einheit: 'm', eigene_position: !!eigene });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (k: string, v: any) => setF({ ...f, [k]: v });
  /* DIE ZUWEISUNG STEHT HIER UND NICHT ERST HINTERHER.
     ========================================================================
     ORIGINALTEXT MARVIN (18.09.2026, zum dritten Mal)
       „immernoch nicht der punkt zur zuweisung der firma, obwohl du es in der
        letzten meldung klar gesagt hast!!!!"

     ER HAT RECHT, UND ES WAR DERSELBE FEHLER WIE BEIM CHIP: ich habe die
     Funktion an einen ORT gehängt, den es in seinem Zustand nicht gibt. Der
     Knopf „Firmen zuweisen" steht an der ZEILE einer Position - und er hat 0
     eigene Positionen. **Ein Weg, den man erst sieht, wenn man schon
     angekommen ist, ist für den, der ihn sucht, keiner.**

     Wer eine eigene Position anlegt, legt sie FÜR jemanden an. Die Firmen
     stehen deshalb in diesem Fenster, mit je einem Preisfeld; wer eine Zahl
     einträgt, hat damit zugewiesen. Der Knopf an der Zeile bleibt für das
     spätere Ändern. */
  const [firmen, setFirmen] = useState<any[]>([]);
  const [preise, setPreise] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!eigene) return;
    api.subunternehmer()
      .then((x: any) => setFirmen((x ?? []).filter((s2: any) => s2.aktiv !== false)))
      .catch(() => { /* ohne Liste geht das Anlegen trotzdem */ });
  }, [eigene]);

  return (
    <Dialog titel={eigene ? 'Eigene Position anlegen' : 'Katalogposition anlegen'}
            laeuft={laeuft} zu={zu} breite={640}>
      <Fehlerkasten lage={fehler} />
      <div className="feldreihe">
        <Text name="oz" titel="Ordnungszahl" pflicht breite={160} wert={f.oz}
              setze={(v) => setze('oz', v)} fehler={fehler} />
        <Auswahl name="einheit" titel="Einheit" pflicht breite={120} leerText=""
                 wert={f.einheit} setze={(v) => setze('einheit', v)} fehler={fehler}
                 optionen={['m', 'm2', 'St', 'LE'].map((x) => ({ wert: x, text: x }))} />
        <Text name="reihenfolge" titel="Reihenfolge" breite={130}
              wert={f.reihenfolge} setze={(v) => setze('reihenfolge', v)}
              fehler={fehler} />
      </div>
      <Text name="text" titel="Text" pflicht breite="100%" wert={f.text}
            setze={(v) => setze('text', v)} fehler={fehler} />
      <Text name="nachweis" titel="Verlangter Nachweis" breite="100%"
            wert={f.nachweis} setze={(v) => setze('nachweis', v)} fehler={fehler} />
      <div className="feldreihe">
        {/* DERSELBE HAKEN WIE IM ÄNDERN-FENSTER, MIT DEMSELBEN NAMEN UND
            DEMSELBEN HINWEIS. Zwei Namen für eine Sache sind der Grund,
            warum Marvin sie nicht gefunden hat. */}
        {/* DIESELBEN 2 HAKEN WIE IM ÄNDERN-FENSTER, mit denselben Namen und
            denselben Hinweisen. Zwei Namen für eine Sache sind der Grund,
            warum Marvin den Bereich 3-mal nicht gefunden hat. */}
        <Haken name="eigene_position" titel="Eigene Position"
               wert={!!f.eigene_position}
               setze={(v) => setze('eigene_position', v)}
               hinweis="Diese Position gibt es im Verzeichnis der Telekom
                        nicht - wir haben sie selbst angelegt. Ordnungszahl,
                        Text und Einheit bestimmen Sie frei. Sie geht in KEINE
                        LEB-Datei. Eine Partnerfirma bekommt sie zur Wahl,
                        sobald für sie unter „Preise je Partner“ ein Preis
                        hinterlegt ist." />
        <Haken name="montage_fest" titel="Feste Montageposition"
               wert={!!f.montage_fest}
               setze={(v) => setze('montage_fest', v)}
               hinweis="Eine Position DER TELEKOM, die in der Auswertung zum
                        Block „Montage, feste Positionen“ zählt." />
        <Haken name="haeufig" titel="Häufig gebraucht" wert={!!f.haeufig}
               setze={(v) => setze('haeufig', v)} />
        <Haken name="nur_innendienst" titel="Nur Innendienst"
               wert={!!f.nur_innendienst} setze={(v) => setze('nur_innendienst', v)} />
      </div>
      {/* WELCHE FIRMEN BEKOMMEN SIE, UND ZU WELCHEM PREIS.
          ==================================================================
          ORIGINALTEXT MARVIN (18.09.2026)
            „immernoch nicht der punkt zur zuweisung der firma"

          Zugewiesen wird über den Preis - sobald eine Firma einen hat,
          bekommt sie die Position im Aufmaß zur Wahl. Leer lassen heißt:
          diese Firma bekommt sie nicht. Nachtragen geht später über den
          Knopf „Firmen zuweisen" an der Zeile. */}
      {eigene && (
        <div style={{ marginTop: 14 }}>
          <div className="klein"><b>Welche Firmen bekommen sie</b></div>
          <p className="klein leise" style={{ margin: '2px 0 8px' }}>
            Zugewiesen wird über den Preis: sobald hier eine Zahl steht,
            bekommt diese Firma die Position im Aufmaß zur Wahl - und jede
            Firma darf einen anderen Preis haben. Leer lassen heißt: diese
            Firma bekommt sie nicht. Nachtragen können Sie es jederzeit über
            „Firmen zuweisen" an der Zeile.
          </p>
          {firmen.length === 0 ? (
            <Leer text="Es sind keine Firmen hinterlegt. Die Position lässt
                        sich trotzdem anlegen; zuweisen können Sie sie später." />
          ) : (
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th style={{ width: 90 }}>Gewerke</th>
                  <th style={{ width: 170 }}>
                    Preis je {f.einheit || 'Einheit'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {firmen.map((s2: any) => (
                  <tr key={s2.id}>
                    <td>
                      <b>{s2.firma}</b>
                      <div className="klein leise">{s2.kurzzeichen}</div>
                    </td>
                    <td className="klein">
                      {(s2.gewerke ?? []).join(', ')
                        || <span className="leise">keine</span>}
                    </td>
                    <td>
                      <input value={preise[s2.id] ?? ''} inputMode="decimal"
                             aria-label={`Preis für ${s2.firma}`}
                             placeholder="nicht zugewiesen"
                             disabled={laeuft}
                             onChange={(e) => setPreise(
                               { ...preise, [s2.id]: e.target.value })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !f.oz || !f.text}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const neu = await api.lvAnlegen(f);
                    /* DIE PREISE GEHEN EINZELN HINAUS, und zwar NACH dem
                       Anlegen: vorher gibt es die Position noch nicht.
                       **Ein Fehler bei einem Preis darf die Position nicht
                       verlieren**, die schon angelegt ist - deshalb steht
                       er im Kasten, und das Fenster bleibt offen. */
                    const offen: string[] = [];
                    for (const [firmaId, wert] of Object.entries(preise)) {
                      if (String(wert).trim() === '') continue;
                      try {
                        await api.subPreisSetzen(firmaId, {
                          lv_position_id: neu.id,
                          einheitspreis: wert,
                          gueltig_ab: heuteIso(),
                        });
                      } catch { offen.push(firmaId); }
                    }
                    if (offen.length) {
                      setFehler(ausFehler(new Error(
                        `Die Position ist angelegt. Bei ${offen.length} `
                        + 'Firma(en) ließ sich der Preis nicht setzen - '
                        + 'tragen Sie ihn über „Firmen zuweisen" an der Zeile '
                        + 'nach.')));
                      return;
                    }
                    fertig();
                  }
                  catch (x) { setFehler(ausFehler(x)); }
                  finally { setLaeuft(false); }
                }}>
          Anlegen
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


// DIE VORDRUCKE STEHEN NICHT MEHR HIER.
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „so, nochmal die frage, wo kommen die Vorlagendokumente rein?"
//
// Er hat zum ZWEITEN Mal gefragt, und das ist die Antwort auf die Frage, ob
// die erste Antwort angekommen ist: nein. Sie standen als SIEBENTER Reiter auf
// diesem Bildschirm, und wer nach einem Ort sucht, an dem Dateien liegen,
// macht keinen Bildschirm auf, auf dem Tabellen gepflegt werden.
//
// Seit Migration 052 sind sie ein eigener Menüpunkt: `seiten/Vordrucke.tsx`.
// Der Reiter ist dabei WEGGEFALLEN und nicht danebengeblieben - zwei Wege zu
// derselben Sache sind kein doppelter Komfort, sondern eine halbe Auskunft.
//
// Das Feld IM Gemeindefenster (`Vorlagenfeld`, weiter oben) bleibt: dort wird
// zugeordnet, im Menüpunkt wird verwaltet.


// ============================================================ Externe Links

/**
 * Die Wege nach draussen.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „Ich will auch gerne den Link zur Telekom als externen Link mit rein zu
 *    nehmen."
 *
 * OHNE ADRESSE STEHT EINE ZEILE NICHT IM MENÜ, und die Liste sagt das an
 * jeder Zeile. Ein Menüpunkt, der ins Leere führt, ist schlimmer als keiner:
 * er sieht aus wie ein Weg.
 */
export function Links({ ich }: { ich: Anmelder }) {
  const [zeilen, setZeilen] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [neu, setNeu] = useState(false);
  const [f, setF] = useState<any>({ name: '', adresse: '', zweck: '',
                                    reihenfolge: 100, fuer_subs: false });
  const darfPflegen = darf(ich, 'link.pflegen');

  const laden = () => api.linksAlle().then(setZeilen)
    .catch((x) => setFehler(ausFehler(x)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function tu(was: () => Promise<any>) {
    setFehler(null);
    try { await was(); await laden(); return true; }
    catch (x) { setFehler(ausFehler(x)); return false; }
  }

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between',
                                      marginBottom: 10 }}>
        <p className="klein leise" style={{ margin: 0, maxWidth: 620 }}>
          Diese Ziele stehen unten im Menü, in einer eigenen Gruppe „Extern".
          Sie öffnen sich in einem neuen Reiter. <b>Eine Zeile ohne Adresse
          erscheint nicht</b> - sie liegt bereit, bis jemand die Adresse
          einträgt.
        </p>
        {darfPflegen && (
          <button className="knopf" onClick={() => setNeu(true)}>
            Link anlegen
          </button>
        )}
      </div>

      <Fehlerkasten lage={fehler} />

      <div className="rollt">
        <table className="tabelle">
          <thead>
            <tr>
              <th style={{ width: 54 }}>Zeichen</th>
              <th>Name</th><th>Adresse</th><th style={{ width: 90 }}>Reihe</th>
              <th>Bereich</th><th>Partnerfirmen</th><th>Nur für</th><th>Im Menü</th><th />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((l) => (
              <tr key={l.id}>
                {/* DAS ZEICHEN. Marvin, 27.08.2026: „mach als Icon das
                    Telekologo rein." Es wird HOCHGELADEN und nicht
                    gezeichnet: eine fremde Marke aus dem Gedächtnis
                    nachzubauen ist immer falsch, und Markenrichtlinien
                    verbieten veränderte Wiedergaben ausdrücklich. */}
                <td>
                  <Linkzeichen l={l} darfPflegen={darfPflegen} neu={laden} />
                </td>
                <td>
                  {darfPflegen
                    ? <input className="feldkurz" defaultValue={l.name}
                             onBlur={(e) => e.target.value !== l.name
                               && tu(() => api.linkAendern(l.id,
                                                { name: e.target.value }))} />
                    : l.name}
                </td>
                <td>
                  {darfPflegen
                    ? <input className="feldlang" defaultValue={l.adresse ?? ''}
                             placeholder="https://..."
                             onBlur={(e) => (e.target.value || '') !== (l.adresse ?? '')
                               && tu(() => api.linkAendern(l.id,
                                                { adresse: e.target.value }))} />
                    : (l.adresse ?? '-')}
                </td>
                <td style={{ width: 90 }}>
                  {darfPflegen
                    ? <input className="feldkurz" type="number"
                             defaultValue={l.reihenfolge}
                             onBlur={(e) => Number(e.target.value) !== l.reihenfolge
                               && tu(() => api.linkAendern(l.id,
                                    { reihenfolge: Number(e.target.value) }))} />
                    : l.reihenfolge}
                </td>
                {/* JE BEREICH (.544). Marvin: „Externe Links kann man drin
                    lassen, muss man aber dann selektieren können." Einer
                    bleibt immer: der Server macht aus keinem Haken beide. */}
                <td className="klein">
                  <div className="reihe" style={{ gap: 4 }}>
                    {(['ne3', 'ne4'] as const).map((b) => {
                      const jetzt: string[] = liste(l.bereiche).length ? liste(l.bereiche) : ['ne3', 'ne4'];
                      const drin = jetzt.includes(b);
                      return (
                        <label key={b} className="gewerk" style={{ cursor: darfPflegen ? 'pointer' : 'default', opacity: drin ? 1 : .5 }}>
                          <input type="checkbox" checked={drin} disabled={!darfPflegen || (drin && jetzt.length === 1)}
                                 onChange={(e) => tu(() => api.linkAendern(l.id, {
                                   bereiche: e.target.checked ? [...jetzt, b] : jetzt.filter((x) => x !== b),
                                 }))} />
                          {' '}{b.toUpperCase()}
                        </label>
                      );
                    })}
                  </div>
                </td>
                {/* WAS FÜR DEN INNENDIENST GEDACHT IST, GEHÖRT NICHT AUF DEN
                    BILDSCHIRM DES MONTEURS. Vorgabe ist deshalb nein. */}
                <td>
                  <Haken name={'sub_' + l.id} titel="sieht ihn auch"
                         wert={!!l.fuer_subs}
                         setze={(v) => tu(() => api.linkAendern(l.id,
                                                    { fuer_subs: v }))} />
                </td>
                {/* UND WELCHE DAVON. „Die Partner der Montage" ist etwas
                    anderes als „alle Partner": der Tiefbauer hat im
                    Auftragnehmerportal der Telekom nichts verloren. Kein
                    Haken heisst ALLE - dieselbe Regel wie im Server. */}
                <td className="klein">
                  <div className="reihe" style={{ gap: 4 }}>
                    {GEWERKE.map((g) => {
                      const drin = liste(l.fuer_gewerke).includes(g);
                      return (
                        <label key={g} className="gewerk"
                               style={{ cursor: darfPflegen ? 'pointer' : 'default',
                                        opacity: drin ? 1 : .5 }}>
                          <input type="checkbox" checked={drin}
                                 disabled={!darfPflegen}
                                 onChange={(e) => tu(() => api.linkAendern(l.id, {
                                   fuer_gewerke: e.target.checked
                                     ? [...liste(l.fuer_gewerke), g]
                                     : liste(l.fuer_gewerke).filter((x: string) => x !== g),
                                 }))} />
                          {' '}{g}
                        </label>
                      );
                    })}
                  </div>
                  {liste(l.fuer_gewerke).length === 0 && (
                    <span className="leise">alle</span>
                  )}
                </td>
                <td className="klein">
                  {!l.adresse
                    ? <span style={{ color: 'var(--neu)' }}>Adresse fehlt</span>
                    : l.aktiv ? 'ja' : 'stillgelegt'}
                </td>
                <td style={{ width: 190 }}>
                  {darfPflegen && (
                    <div className="reihe">
                      <button className="knopf klein"
                              onClick={() => tu(() => api.linkAendern(l.id,
                                                   { aktiv: !l.aktiv }))}>
                        {l.aktiv ? 'stilllegen' : 'wieder anzeigen'}
                      </button>
                      <button className="knopf klein"
                              onClick={() => tu(() => api.linkWeg(l.id))}>
                        löschen
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {zeilen.length === 0 && (
              <tr><td colSpan={8} className="klein leise">
                Noch kein Außenziel eingetragen.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {neu && (
        <Dialog titel="Externen Link anlegen" zu={() => setNeu(false)} breite={620}>
          <Fehlerkasten lage={fehler} />
          <div className="feldreihe">
            <Text name="name" titel="Name im Menü" wert={f.name} pflicht
                  breite={240}
                  setze={(v) => setF({ ...f, name: v })} />
            <Text name="adresse" titel="Adresse" wert={f.adresse} breite={340}
                  hinweis={'Mit https:// davor. Darf leer bleiben - dann steht '
                         + 'die Zeile bereit und erscheint noch nicht im Menü.'}
                  setze={(v) => setF({ ...f, adresse: v })} />
            <Text name="zweck" titel="Wofür" wert={f.zweck} breite={340}
                  setze={(v) => setF({ ...f, zweck: v })} />
            <Haken name="fuer_subs" titel="Partnerfirmen sehen ihn auch"
                   wert={f.fuer_subs}
                   setze={(v) => setF({ ...f, fuer_subs: v })} />
          </div>
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf haupt" disabled={!f.name.trim()}
                    onClick={async () => {
                      if (await tu(() => api.linkAnlegen(f))) {
                        setNeu(false);
                        setF({ name: '', adresse: '', zweck: '',
                               reihenfolge: 100, fuer_subs: false });
                      }
                    }}>
              Anlegen
            </button>
            <button className="knopf" onClick={() => setNeu(false)}>
              Abbrechen
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}


/**
 * Ein Leistungsverzeichnis aus einer Datei einlesen.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „für Leistungspositionen gibt es neue Preise. Preise sind aber nicht drin.
 *    Dann fällt das zumindest raus. aber der muss zumindest mal dann die Datei
 *    einlesen können. Wo sind die Sachen abgelegt dazu?"
 *
 * ZWEI SCHRITTE, UND DER ERSTE SCHREIBT NICHTS. Derselbe Aufbau wie beim
 * Telekom-Import, und aus demselben Grund: der Katalog ist über Monate an
 * echten Fällen geschliffen worden, und was eine Datei daran ändert, sieht man
 * VORHER.
 *
 * UND DER PREIS STEHT IM KASTEN, obwohl er verworfen wird. Wer eine Datei mit
 * Einheitspreisen hochlädt und danach einen Katalog ohne Preise sieht, hält
 * das für einen Fehler. Es ist keiner: in diesem System wird kein Preis
 * gespeichert.
 */
function LvEinlesen({ zu, fertig }: { zu: () => void; fertig: () => void }) {
  const [datei, setDatei] = useState<File | null>(null);
  const [plan, setPlan] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [fertigText, setFertigText] = useState<string | null>(null);

  async function lesen(d: File) {
    setDatei(d); setLaeuft(true); setFehler(null); setPlan(null);
    try { setPlan(await api.lvEinlesen(d, false)); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function anwenden() {
    if (!datei) return;
    setLaeuft(true); setFehler(null);
    try {
      const e = await api.lvEinlesen(datei, true);
      setFertigText(e.klartext);
      setPlan(e);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Leistungsverzeichnis einlesen" breite={780} laeuft={laeuft}
            zu={fertigText ? fertig : zu}>
      <Fehlerkasten lage={fehler} />

      {!plan && (
        <>
          <p className="klein leise" style={{ marginTop: 0 }}>
            Eine Tabelle mit einer Spalte für die Ordnungszahl, den Text und
            die Einheit. CSV oder XLSX; die Spalten werden an ihrer
            Überschrift erkannt, nicht an ihrer Stelle.
          </p>
          <input type="file" accept=".csv,.xlsx,.xls,.txt"
                 onChange={(e) => {
                   const d = e.target.files?.[0];
                   if (d) lesen(d);
                 }} />
        </>
      )}

      {plan && (
        <>
          <div className="kachelreihe" style={{ marginTop: 4 }}>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--laufend)' }}>
                {plan.neu?.length ?? 0}
              </div>
              <div className="was">kommen dazu</div>
            </div>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--neu)' }}>
                {plan.geaendert?.length ?? 0}
              </div>
              <div className="was">werden geändert</div>
            </div>
            <div className="kachel">
              <div className="wert">{plan.gleich ?? 0}</div>
              <div className="was">bleiben, wie sie sind</div>
            </div>
            <div className="kachel">
              <div className="wert">{plan.fehlen?.length ?? 0}</div>
              <div className="was">stehen nicht in der Datei</div>
            </div>
          </div>

          <p className="klein leise" style={{ marginTop: 8 }}>
            Erkannt als Ordnungszahl: <b>{plan.spalten?.oz ?? '–'}</b>,
            als Text: <b>{plan.spalten?.text ?? '–'}</b>,
            als Einheit: <b>{plan.spalten?.einheit ?? 'keine Spalte gefunden'}</b>.
          </p>

          {/* DER PREIS. Er wird gezählt und dann verworfen, und das steht da. */}
          {plan.preise?.zeilen > 0 && (
            <div className="hinweis" style={{ marginTop: 10 }}>
              <b>{plan.preise.zeilen} Zeilen tragen einen Preis. Er wird nicht
              übernommen.</b>
              In diesem System wird kein Preis gespeichert - weder Einheits-
              noch Gesamtpreis. Das ist keine Lücke, sondern eine Festlegung:
              die Abrechnung entsteht aus Mengen, und die Preise liegen beim
              Auftraggeber.
              <div className="klein" style={{ marginTop: 4 }}>
                Verworfene Spalten: {plan.preise.spalten?.join(', ')}
                {plan.preise.beispiel && `, zum Beispiel ${plan.preise.beispiel}`}
              </div>
            </div>
          )}

          {(plan.verworfen ?? []).map((v: any, i: number) => (
            <div key={i} className="hinweis warnung" style={{ marginTop: 8 }}>
              <b>{v.anzahl === 1 ? 'Eine Zeile' : `${v.anzahl} Zeilen`}</b>
              {v.grund}
              {v.beispiel && (
                <div className="klein" style={{ marginTop: 4 }}>{v.beispiel}</div>
              )}
            </div>
          ))}

          {plan.geaendert?.length > 0 && (
            <>
              <p className="klein" style={{ marginTop: 12, marginBottom: 6 }}>
                <b>Was sich ändert.</b>
              </p>
              <table className="liste">
                <thead>
                  <tr><th style={{ width: 110 }}>OZ</th><th style={{ width: 80 }}>Feld</th>
                      <th>steht im Katalog</th><th>steht in der Datei</th></tr>
                </thead>
                <tbody>
                  {plan.geaendert.slice(0, 30).map((a: any, i: number) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                      <td className="einfarbig">{a.oz}</td>
                      <td className="klein">{a.feld}</td>
                      <td className="klein">{a.alt}</td>
                      <td className="klein leise">{a.neu}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {plan.fehlen?.length > 0 && (
            <div className="hinweis" style={{ marginTop: 10 }}>
              <b>{plan.fehlen.length} Positionen stehen im Katalog und nicht in
              der Datei</b>
              Sie bleiben stehen. An ihnen hängen Aufmasszeilen alter
              Abrechnungen; wer sie loswerden will, setzt sie einzeln ausser
              Kraft.
              <div className="klein" style={{ marginTop: 4 }}>
                {plan.fehlen.slice(0, 12).map((f: any) => f.oz).join(', ')}
                {plan.fehlen.length > 12 && ` und ${plan.fehlen.length - 12} weitere`}
              </div>
            </div>
          )}

          {fertigText && (
            <div className="hinweis" style={{ marginTop: 12 }}>{fertigText}</div>
          )}

          <div className="knopfreihe" style={{ marginTop: 14 }}>
            {!fertigText && (
              <button className="knopf haupt" disabled={laeuft || !plan.gelesen}
                      onClick={anwenden}>
                {plan.neu?.length || plan.geaendert?.length
                  ? `${plan.neu?.length ?? 0} anlegen, ${plan.geaendert?.length ?? 0} ändern`
                  : 'Nichts zu tun'}
              </button>
            )}
            <button className="knopf" onClick={fertigText ? fertig : zu}>
              {fertigText ? 'Schließen' : 'Abbrechen'}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}


/**
 * Das Zeichen an einem Aussenziel.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „mach als Icon das Telekologo rein"
 *
 * ES WIRD HOCHGELADEN UND NICHT GEZEICHNET. Das Telekom-„T" ist eine
 * eingetragene Marke. Sie zu benutzen, um auf das Portal DER TELEKOM zu
 * zeigen, ist in Ordnung - sie aus dem Gedächtnis NACHZUBAUEN ist es nicht:
 * eine selbstgemalte Fassung einer fremden Marke ist immer falsch, und
 * Markenrichtlinien verbieten veränderte Wiedergaben ausdrücklich.
 *
 * SOLANGE KEINES DA IST, TRÄGT DIE FARBE DEN LINK: ein Feld in der Kennfarbe
 * mit dem Anfangsbuchstaben darauf. Das ist keine Marke und sieht auch nicht
 * so aus - es sagt nur „hier geht es woanders hin".
 */
function Linkzeichen({ l, darfPflegen, neu }: any) {
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function hoch(datei: File) {
    setLaeuft(true); setFehler(null);
    try { await api.linkZeichenHochladen(l.id, datei); setOffen(false); await neu(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <div>
      <div className="reihe" style={{ gap: 4, alignItems: 'center' }}>
        {l.hat_icon
          ? <img src={api.linkZeichenAdresse(l.id)} alt="" width={24}
                 height={24} style={{ objectFit: 'contain' }} />
          : <span className="linkzeichen ersatz"
                  style={{ width: 24, height: 24, borderRadius: 4,
                           display: 'flex', alignItems: 'center',
                           justifyContent: 'center', fontSize: 12,
                           fontWeight: 700,
                           background: l.farbe ?? 'var(--flaeche)',
                           color: l.farbe ? '#fff' : 'var(--text-leise)' }}>
              {String(l.name ?? '?').trim().charAt(0).toUpperCase()}
            </span>}
        {darfPflegen && (
          <button className="knopf klein" disabled={laeuft}
                  title="Ein Zeichen hochladen"
                  onClick={() => setOffen(!offen)}>
            {offen ? 'zu' : '…'}
          </button>
        )}
      </div>

      {offen && darfPflegen && (
        <Dialog titel={`Zeichen für „${l.name}"`} laeuft={laeuft}
                zu={() => setOffen(false)} breite={520}>
          <Fehlerkasten lage={fehler} />
          <p className="klein leise" style={{ marginTop: 0 }}>
            PNG, JPG oder WEBP, quadratisch, höchstens 2 MB. <b>Keine
            SVG</b> - die darf Programmcode enthalten, den der Browser
            ausführt.
          </p>
          <p className="klein leise">
            Bei einer fremden Marke gehört die ECHTE Datei hierher. Eine
            nachgemalte Fassung ist immer falsch, und Markenrichtlinien
            verbieten veränderte Wiedergaben ausdrücklich.
          </p>
          <Abwurf titel="Das Zeichen hierher" klein laeuft={laeuft}
                  laufText="Wird abgelegt" accept=".png,.jpg,.jpeg,.webp"
                  hinweis="Ziehen oder tippen."
                  nimm={(x) => { if (x[0]) hoch(x[0].datei); }} />
          <div className="feldreihe" style={{ marginTop: 10 }}>
            <Text name={'farbe_' + l.id} titel="Kennfarbe" breite={160}
                  wert={l.farbe ?? ''}
                  hinweis="Gilt, solange kein Zeichen da ist. Zum Beispiel #E20074."
                  setze={async (v) => {
                    setFehler(null);
                    try { await api.linkAendern(l.id, { farbe: v }); await neu(); }
                    catch (f) { setFehler(ausFehler(f)); }
                  }} />
          </div>
          <div className="reihe" style={{ marginTop: 12 }}>
            {l.hat_icon && (
              <button className="knopf" disabled={laeuft}
                      onClick={async () => {
                        setLaeuft(true);
                        try { await api.linkZeichenWeg(l.id); await neu(); }
                        catch (f) { setFehler(ausFehler(f)); }
                        finally { setLaeuft(false); }
                      }}>
                Zeichen abnehmen
              </button>
            )}
            <div className="wachsen" />
            <button className="knopf haupt" onClick={() => setOffen(false)}>
              Schließen
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}


/**
 * Welche Gewerke eine Leistungsposition wählen dürfen.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „du musst die gewerke tiefbau und Montage sauber trennen."
 *
 * DREI HAKEN UND EINE ANSAGE. „Keiner gesetzt" ist hier kein leerer Zustand,
 * sondern die Vorgabe: die Position steht bei jedem. Ein Feld, das das nicht
 * hinschreibt, liest sich wie ein Pflegefehler - und dann setzt jemand
 * vorsichtshalber alle drei, was etwas ganz anderes bedeutet: er hat
 * entschieden.
 *
 * DIE DOKUMENTATION (DK) STEHT NICHT DABEI. Sie ist unsere Abgabe an die
 * Telekom und keine Bauleistung, die jemand aufmisst.
 */
function GewerkeWahl({ wert, setze }: { wert: string; setze: (w: string) => void }) {
  const drin = wert.split(',').map((x) => x.trim()).filter(Boolean);
  const um = (g: string) => {
    const neu = drin.includes(g) ? drin.filter((x) => x !== g) : [...drin, g];
    // Immer in derselben Reihenfolge: sonst zaehlt der Entwurfsvergleich eine
    // Aenderung, wo nur die Reihenfolge eine andere ist.
    setze(['TB', 'EB', 'SP'].filter((x) => neu.includes(x)).join(','));
  };
  return (
    <div className="feld" style={{ width: '100%' }}>
      <label>Welche Gewerke wählen dürfen</label>
      <div className="reihe">
        {[['TB', 'Tiefbau'], ['EB', 'Einblasen'], ['SP', 'Spleißen']].map(
          ([k, t]) => (
            <button key={k} type="button"
                    className={'knopf filter' + (drin.includes(k!) ? ' gewaehlt' : '')}
                    aria-pressed={drin.includes(k!)}
                    onClick={() => um(k!)}>
              {t}
            </button>
          ))}
      </div>
      <small className="leise">
        {drin.length
          ? `Nur ${drin.length === 1 ? 'dieses Gewerk bekommt' : 'diese Gewerke bekommen'} `
            + 'die Position zur Auswahl.'
          : 'Keines gesetzt heißt: die Position steht bei jedem. Das ist die '
            + 'Vorgabe - wer sie enger zieht, nimmt sie jemandem weg.'}
      </small>
    </div>
  );
}


/**
 * PREISE JE PARTNER.
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (18.09.2026)
 *   „mir fehlen immernoch bei der montage die eigenen positionen zur auswahl.
 *    diese posionen trage ich selbst ein mit eigenen preisen zu jedem sub. wo
 *    ist der bereich?"
 *
 * DIE POSITIONEN GAB ES, DIE PREISE NICHT. Was ein Partner für eine eigene
 * Position bekommt, stand bis zum 18.09.2026 an keiner Stelle dieses Systems.
 *
 * WO DIE POSITIONEN ENTSTEHEN, HAT SICH AM SELBEN TAG NOCH EINMAL GEÄNDERT.
 * Marvin: „du hast deine arbeit nicht gemacht! es ist überhaupt nicht da was
 * du gebaut hast." Sie entstanden bis dahin über einen HAKEN im Fenster
 * „ändern" einer Position des Telekom-Katalogs - und im Fenster „anlegen"
 * gab es den Haken gar nicht. Seitdem ist es ein eigener Reiter neben diesem:
 * **„Eigene Positionen".** Er hat 3-mal nach einem ORT gefragt.
 *
 * EIN PREIS WIRD NICHT ÜBERSCHRIEBEN, SONDERN ABGELÖST. Wer einen neuen
 * einträgt, setzt ein Datum dazu; der alte bekommt sein Ende auf denselben
 * Tag. **Ein überschriebener Preis nimmt jeder alten Abrechnung ihre
 * Grundlage** - und die Frage, was im März galt, stellt sich genau dann, wenn
 * eine Rechnung vom März auf dem Tisch liegt.
 *
 * DIE LISTE ZEIGT AUCH DIE POSITIONEN OHNE PREIS. Eine, die nur die
 * bepreisten nennt, beantwortet „was fehlt noch" nicht - und das ist die
 * Frage, wegen der man diesen Bildschirm aufmacht.
 */
function Partnerpreise() {
  const [firmen, setFirmen] = useState<any[]>([]);
  const [firma, setFirma] = useState('');
  const [d, setD] = useState<any>(null);
  const [nurOhne, setNurOhne] = useState(false);
  const [ab, setAb] = useState(heuteIso());
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.subunternehmer().then((f: any) => setFirmen(f ?? [])).catch(() => {});
  }, []);

  async function laden(f: string) {
    if (!f) { setD(null); return; }
    try { setD(await api.subPreise(f)); setFehler(null); }
    catch (x) { setFehler(ausFehler(x)); setD(null); }
  }
  useEffect(() => { void laden(firma); }, [firma]);
  /* UND NACH JEDEM SCHREIBVORGANG. Wer einen Preis setzt, ändert genau das,
     was diese Liste zeigt; ohne diese Zeile stünde der alte Preis noch da. */
  useFrisch(() => { void laden(firma); });

  async function setze(positionId: string) {
    setLaeuft(positionId); setFehler(null);
    try {
      await api.subPreisSetzen(firma, {
        lv_position_id: positionId,
        einheitspreis: entwurf[positionId] ?? '',
        gueltig_ab: ab,
      });
      setEntwurf((e) => { const n = { ...e }; delete n[positionId]; return n; });
      await laden(firma);
    } catch (x) { setFehler(ausFehler(x)); }
    finally { setLaeuft(null); }
  }

  const zeilen = (d?.stand ?? []).filter(
    (z: any) => !nurOhne || z.einheitspreis === null);

  return (
    <Karte titel="Preise je Partner">
      <div className="leib">
        <p className="klein leise" style={{ marginTop: 0 }}>
          Was ein Partner für eine Position bekommt. Das ist etwas anderes als
          der Rahmenvertragspreis nebenan: der sagt, was <b>wir</b> bekommen.
          Ein neuer Preis überschreibt den alten nicht, sondern löst ihn zum
          genannten Tag ab - eine alte Abrechnung behält damit ihre Grundlage.
          Diese Preise gehen in keine LEB-Datei, und der Partner sieht sie
          nicht.
        </p>

        <Fehlerkasten lage={fehler} />

        <div className="feldreihe" style={{ marginBottom: 12 }}>
          <label className="feldpaar" style={{ minWidth: 260 }}>
            <span>Firma</span>
            <select value={firma} onChange={(e) => setFirma(e.target.value)}>
              <option value="">bitte wählen</option>
              {firmen.map((f: any) => (
                <option key={f.id} value={f.id}>{f.firma}</option>
              ))}
            </select>
          </label>
          <label className="feldpaar" style={{ minWidth: 170 }}>
            <span>gilt ab</span>
            <input type="date" value={ab}
                   onChange={(e) => setAb(e.target.value)} />
          </label>
          <label className="feldpaar" style={{ minWidth: 200 }}>
            <span>Anzeige</span>
            <select value={nurOhne ? 'ohne' : 'alle'}
                    onChange={(e) => setNurOhne(e.target.value === 'ohne')}>
              <option value="alle">alle Positionen</option>
              <option value="ohne">nur die ohne Preis</option>
            </select>
          </label>
        </div>

        {!firma && (
          <Leer text="Wählen Sie oben eine Firma." />
        )}

        {firma && d && (
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 110 }}>OZ</th>
                <th>Position</th>
                <th style={{ width: 60 }}>Einheit</th>
                <th style={{ width: 120 }}>gilt heute</th>
                <th style={{ width: 200 }}>neuer Preis</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z: any) => (
                <tr key={z.lv_position_id} style={{ cursor: 'default' }}>
                  <td className="klein">
                    <b>{z.oz}</b>
                    {z.montage_fest && (
                      <div className="leise">eigene Position</div>
                    )}
                  </td>
                  <td className="klein">{z.text}</td>
                  <td className="klein">{z.einheit}</td>
                  <td className="klein">
                    {z.einheitspreis === null
                      ? <span className="leise">kein Preis</span>
                      : (
                        <>
                          {Number(z.einheitspreis).toLocaleString('de-DE',
                            { minimumFractionDigits: 2 })} €
                          <div className="leise">
                            seit {String(z.gueltig_ab).slice(0, 10)
                                    .split('-').reverse().join('.')}
                          </div>
                        </>
                      )}
                  </td>
                  <td className="knopfleiste">
                    {d.darfPflegen ? (
                      <>
                        <input style={{ width: 90 }} inputMode="decimal"
                               placeholder="leer = weg"
                               value={entwurf[z.lv_position_id] ?? ''}
                               onChange={(e) => setEntwurf((x) => ({
                                 ...x, [z.lv_position_id]: e.target.value }))} />
                        <button className="knopf klein"
                                disabled={laeuft === z.lv_position_id
                                  || entwurf[z.lv_position_id] === undefined}
                                onClick={() => void setze(z.lv_position_id)}>
                          {laeuft === z.lv_position_id ? '…' : 'setzen'}
                        </button>
                      </>
                    ) : <span className="leise klein">nur lesend</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {firma && d && zeilen.length === 0 && (
          <Leer text="Zu jeder Position steht ein Preis." />
        )}
      </div>
    </Karte>
  );
}


/**
 * WOHIN TELEGRAM WIRKLICH SCHICKT (.241), unter dem Feld: „Thema 3" oder
 * „allgemeines Thema". Dieselbe Deutung wie `zielAusChatId` am Server:
 * „-100….3", „-100…:3", „/3" und „#3" sind Thema 3, „.0" ist keins.
 */
function themaText(w: unknown): string {
  const s = String(w ?? '').trim();
  if (!s) return '';
  const m = /^(-?\d+)\s*(?:\.0+|[.:/#]\s*(\d+))?$/.exec(s);
  if (!m) return '';
  const t = Number(m[2] ?? 0);
  return t > 0 ? `, geht in Thema ${t}` : ', geht ins allgemeine Thema';
}
